import { getAccessToken, logout, sessionHeaders, validateSession } from './auth';
import { assembleYieldSheet, invoiceImageList, transportImageList } from '../pages/yieldSheetCalc';

// ── Neon (Postgres) queries via Netlify function ──────────────────────────────

// ⚠️ A STATUS CODE IS NOT AN ERROR MESSAGE. Every handler in neon.js answers a
// refusal with a written reason - "Enter the invoice amount before submitting",
// "That crabber trip is already on approval #12", "Only the person who started
// this can edit it" - and throwing on res.ok threw every one of them away and
// showed "Neon error: 400" instead. Mercedes was blocked by exactly that: the
// invoice she was sending had no amount on it, nothing said so, and it read as
// the button being broken. Read the body, then throw.
// ⚠️ THE REST OF THE BODY RIDES ON THE ERROR, not just the sentence. A refusal
// often carries the thing you need to act on it - ap_document_submit returns
// duplicateOf so the screen can offer "open that bill" instead of naming a number
// nobody can find, which is exactly how Matt Sr was told his invoice was a
// duplicate of a bill no list would render. Throwing the body away made every
// refusal a dead end.
async function neonError(res) {
  let msg = '';
  let body = null;
  try {
    body = await res.json();
    msg = body && (body.error || body.message);
  } catch (_) { /* not JSON - fall back to the status */ }
  const err = new Error(msg || `Neon error: ${res.status}`);
  err.status = res.status;
  if (body && typeof body === 'object') err.body = body;
  return err;
}

export async function neonQuery(query, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/neon`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, params }),
  });
  if (res.status === 401 || res.status === 511) {
    logout();
    window.location.reload();
    throw new Error('Session expired');
  }
  if (!res.ok) throw await neonError(res);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  // A bill moved (approved, sent back, submitted, deleted, edited, paid): tell the
  // "waiting for your approval" banner now instead of leaving it to its next poll.
  if (/^ap_document_(action|submit|create|delete|update|mark_paid|mark_entered)$/.test(query)) {
    try { window.dispatchEvent(new Event('cpx:approvals-changed')); } catch (_) { /* never fail a save over a banner */ }
  }
  // Most handlers return { rows, rowCount }. Some (auth/setup) return a flat
  // object like { code, emailSent }. Pass the whole payload through when
  // there's no rows field so non-row responses are readable.
  return data.rows !== undefined ? data.rows : data;
}

// ── Claude Code panel ────────────────────────────────────────────────────────
// The in-app build requests (src/components/ClaudeBuild.js). Actions: access |
// chats | chat | send | rename | archive | retry.
//
// Deliberately NOT routed through neonQuery: a 401 there calls logout() and
// reloads the page, which is right for a data screen and wrong here. `access`
// is the first call the panel makes on every page load, so a stale token would
// reload the app out from under a component the user cannot even see.
export async function claudeBuild(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/claude-build`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...params }),
  });
  let data = {};
  try { data = await res.json(); } catch (_) { /* fall through to the status */ }
  if (!res.ok) throw new Error(data.error || `Claude Code error: ${res.status}`);
  return data;
}

// ── Ask ──────────────────────────────────────────────────────────────────────
// The CPx Ask page (netlify/functions/ask.js). Like claudeBuild, NOT routed through
// neonQuery: a 401 there logs out and reloads, which is wrong for a polling chat.
export async function askApi(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/ask`, { method: 'POST', headers, body: JSON.stringify({ action, ...params }) });
  let data = {};
  try { data = await res.json(); } catch (_) { /* fall through to the status */ }
  if (!res.ok) throw new Error(data.error || `Ask error: ${res.status}`);
  return data;
}

// ── Marketing Brain ──────────────────────────────────────────────────────────
// Send (or preview) the daily marketing brief. dryRun returns the rendered HTML
// as text so the dashboard can pop it in a tab without emailing anyone.
// NOTE the worker is `marketing-brief`, not `marketing-brief-scheduled` —
// Netlify 403s POSTs to any function carrying a cron schedule, so the schedule
// lives on a separate trigger function.
export async function sendMarketingBrief({ dryRun = false, date, to } = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/marketing-brief`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dryRun, date, to }),
  });
  if (!res.ok) throw new Error(`Marketing brief error: ${res.status}`);
  const ct = res.headers.get('content-type') || '';
  return ct.includes('text/html') ? res.text() : res.json();
}

// ── Claude (Anthropic) via the claude-proxy function ──────────────────────────
// The API key lives only in the function's environment — never in the bundle.
// Callers pass the same body they would have sent to /v1/messages (model,
// max_tokens, system, tools, messages) and get the message back unchanged, so
// page-side tool loops that read stop_reason / content still work.
//
// ⚠ IT RUNS AS A JOB, NOT AS ONE REQUEST, AND THAT IS THE POINT. A Netlify
// synchronous function is killed by the gateway at ~26 seconds — netlify.toml's
// `timeout` does not extend it — so a long answer came back as a bare
// "Claude error: 504" from the gateway's own timeout page. These callers are the
// heavy ones (the Meta Builder asks for 4000 tokens of ad copy across 4-10
// variants; Landing Pages asks for 8000), so every one of them now starts a job
// that runs in a background function and polls for the answer. The shape of what
// goes out and what comes back is unchanged, so no caller needed editing.
//
// The two brain chats deliberately still call the synchronous path: they run
// 900-1400 max_tokens, answer in seconds, and would only pay the poll latency.
const CLAUDE_POLL_MS = 1000;
const CLAUDE_FIRST_POLL_MS = 400; // a short answer should not wait a whole second
const CLAUDE_MAX_WAIT_MS = 5 * 60 * 1000;

function fnBase() {
  return window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
}

async function claudeProxyPost(payload) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${fnBase()}/claude-proxy`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (res.status === 401 || res.status === 511) {
    logout();
    window.location.reload();
    throw new Error('Session expired');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Claude error: ${res.status}`);
  return data;
}

export async function callClaude(body) {
  const { jobId } = await claudeProxyPost({ ...body, action: 'start' });
  if (!jobId) throw new Error('Claude did not start');

  const started = Date.now();
  let wait = CLAUDE_FIRST_POLL_MS;
  for (;;) {
    await new Promise((r) => setTimeout(r, wait));
    wait = CLAUDE_POLL_MS;
    const job = await claudeProxyPost({ action: 'poll', jobId });
    if (job.status === 'done') return job.message;
    if (job.status === 'error') throw new Error(job.error || 'Claude API error');
    if (Date.now() - started > CLAUDE_MAX_WAIT_MS) {
      throw new Error('Claude is taking too long. Try a shorter brief.');
    }
  }
}

// PR-G: every bc-proxy call should go through here so it carries the admin session
// token (Authorization: Bearer) for the bc-proxy auth gate. Returns the raw Response
// so callers keep using res.ok / res.status / res.json() exactly as before.
export function bcProxy(payload) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(`${base}/bc-proxy`, { method: 'POST', headers, body: JSON.stringify(payload) });
}

// Anthropic Messages API — ALWAYS go through this, never call api.anthropic.com from
// the browser. The key used to ship inside the public bundle via
// REACT_APP_ANTHROPIC_API_KEY and was byte-identical to the server key;
// anthropic-proxy now holds it server-side. Body is the unchanged /v1/messages
// payload and the raw Response is returned, so tool-use loops and existing
// res.ok / res.json() parsing keep working exactly as before.
export function anthropicProxy(payload) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(`${base}/anthropic-proxy`, { method: 'POST', headers, body: JSON.stringify(payload) });
}

// ── QuickBooks Online ─────────────────────────────────────────────────────────
// Two endpoints on purpose: qbo-oauth owns the connection
// (credentials, consent, disconnect) and qbo-proxy owns reading. Both need the staff
// session - there is no ungated read, because these return four companies' ledgers.
export async function qboOAuth(action, params = {}) {
  return qboCall('qbo-oauth', action, params);
}

export async function qboProxy(action, params = {}) {
  return qboCall('qbo-proxy', action, params);
}

async function qboCall(fn, action, params) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/${fn}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) throw new Error(data.error || `QuickBooks error: ${res.status}`);
  return data;
}

// ── YouTube upload (Send to YouTube) ──────────────────────────────────────────
// All actions go through the youtube-proxy function (OAuth + dispatch). Like the
// other marketing proxies (meta-organic), it's CORS-open and the page itself is
// behind the app login + role gating.
export async function youtubeProxy(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/youtube-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) throw new Error(data.error || `YouTube error: ${res.status}`);
  return data;
}

export async function neonRawSelect(sql) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/neon`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query: 'raw_select', sql }),
  });
  if (res.status === 401 || res.status === 511) {
    logout();
    window.location.reload();
    throw new Error('Session expired');
  }
  if (!res.ok) throw await neonError(res);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return { rows: data.rows, rowCount: data.rowCount, sql: data.sql };
}

// ── Knowledge Capture (avatar-interview KB pipeline) ──────────────────────────
function functionsBase() {
  return window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
}

async function postFunction(name, payload) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${functionsBase()}/${name}`, {
    method: 'POST', headers, body: JSON.stringify(payload || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) throw new Error(data.error || data.detail || `${name} failed (${res.status})`);
  return data;
}

// Create a Tavus conversation for an interview → { conversation_url, conversation_id }
export async function kcStartInterview(interviewId, interviewerName) {
  return postFunction('tavus-start', { interviewId, interviewerName });
}

// Pull the transcript from Tavus + build the draft (belt-and-suspenders for a
// missed webhook). Background fn → returns ~immediately; the page polls for the draft.
export async function kcIngestTranscript(interviewId) {
  return postFunction('tavus-ingest-background', { interviewId });
}

// Publish a reviewed draft: commit the .md to cpx-claude-ai + promote any
// approved Jimmy snippets. → { ok, path, commit, jimmyInserted }
export async function kcPublishDraft(payload) {
  return postFunction('knowledge-publish', payload);
}

// ── Contact Board (Communications) ────────────────────────────────────────────
// Counts by channel for a window, in the order Rachael set. The window is passed as
// real instants worked out in Eastern time by the page, so the server never has to
// guess a timezone. Takes a few seconds when the phone side is included, because that
// is read live from the contact centre rather than from a mirror that would miss
// every abandoned call.
export async function fetchContactBoard(params = {}) {
  return postFunction('contact-board', params);
}

// Order Alerts (board 2020): pickup / last call settings, date overrides, the alert
// list and the chase sweep. netlify/functions/cp-alerts.js; Admin / Manager / IT.
export async function orderAlerts(action, params = {}) {
  return postFunction('cp-alerts', { action, ...params });
}

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5009';

function authHeaders(extra = {}) {
  const token = getAccessToken();
  return token ? { access_token: token, ...extra } : { ...extra };
}

// A 401 from the /api proxy does NOT prove the CPx session is dead: the proxy
// passes the production API's own 401s straight through, and a call that forgot
// the session header gets one too. Logging out on any of them put Greg in a
// sign-in loop on 2026-10-01 (an order with no notes in Neon fell back to a
// header-less get(), got 401, and every Windows Hello sign-in was undone three
// seconds later). So ask the server whether the session is really gone first.
let _unauthCheck = null;
function handleUnauthorized(res) {
  if (res.status !== 401 && res.status !== 511) return;
  if (_unauthCheck) return;
  _unauthCheck = validateSession()
    .then(valid => { if (!valid) window.location.reload(); })
    .catch(() => {})
    .finally(() => { _unauthCheck = null; });
}

// Safe GET that doesn't logout on 401
async function getSafe(path, params = {}) {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined) url.searchParams.append(k, v);
  });
  const res = await fetch(url.toString());
  if (!res.ok) return null;
  return res.json();
}

async function get(path, params = {}) {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined) url.searchParams.append(k, v);
  });
  // The /api proxy requires the staff session token (see apiProxyGet).
  const res = await fetch(url.toString(), { headers: authHeaders() });
  if (!res.ok) {
    handleUnauthorized(res);
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.json();
}

// Like get(), but routes through the /api/ proxy (api-proxy.js injects the
// service-account auth server-side). Use for prod endpoints with
// CrabAdminTokenAuthentication — calling api-dev directly from the browser fails
// ("The given header was not found").
//
// The proxy is session-gated and GET-only (it forwards with FULL production
// admin rights), so every call must carry the staff session token. Route all
// /api/* reads through this helper rather than a bare fetch — a call site that
// forgets the header gets a 401, and one that needs a write won't get one.
export async function apiProxyGet(path, params = {}) {
  const url = new URL('/api' + path, window.location.origin);
  Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined) url.searchParams.append(k, v); });
  const res = await fetch(url.toString(), { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

const apiGet = apiProxyGet;

// Fetch /Orders/OrderInformation, turning the backend's ungraceful
// "order doesn't exist" behavior into a clean not-found signal. The SP
// dereferences null and the controller throws a System.NullReferenceException
// (HTTP 500) for any order id that has no matching order — instead of a 404 —
// so a plain `500` reaching the UI looks like the server is down. Detect that
// exact signature and throw a friendly error flagged `notFound`; anything else
// (real 500s, 502/503/504, network) still surfaces as a genuine failure.
async function getOrderInformation(orderId) {
  // Route through the /api proxy (api-proxy.js injects the service-account
  // access_token server-side). Calling api-dev directly from the browser sends
  // no auth header, so the backend 500s at the auth filter ("The given header
  // was not found") for EVERY order — never reaching the not-found path below.
  const url = new URL('/api/Orders/OrderInformation', window.location.origin);
  url.searchParams.append('orderId', orderId);
  const res = await fetch(url.toString(), { headers: authHeaders() });
  if (res.ok) return res.json();
  handleUnauthorized(res);
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON error body */ }
  const msg = body?.exceptionMessage || body?.message || '';
  const isNotFound = res.status === 500 &&
    (body?.exceptionType === 'System.NullReferenceException' ||
     /object reference not set/i.test(msg));
  if (isNotFound) {
    const err = new Error(`Order #${orderId} was not found. Double-check the order number and try again.`);
    err.notFound = true;
    throw err;
  }
  throw new Error(`${res.status} ${res.statusText}`.trim() || `Request failed (${res.status})`);
}

// Cache slow, infrequently-changing API responses in sessionStorage
function cachedGet(path, params = {}, ttlMs = 5 * 60 * 1000) {
  const key = `api_cache:${path}:${JSON.stringify(params)}`;
  try {
    const cached = JSON.parse(sessionStorage.getItem(key));
    if (cached && Date.now() - cached.ts < ttlMs) return Promise.resolve(cached.data);
  } catch { /* ignore */ }
  return get(path, params).then(data => {
    try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch { /* ignore */ }
    return data;
  });
}

export async function post(path, body) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    handleUnauthorized(res);
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.json();
}

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pct = (n) => `${Number(n || 0).toFixed(2)}%`;

const getAlertType = (v) => {
  const n = Number(v ?? 0);
  if (n === 0) return 'zero';
  if (n > 500) return 'danger';
  return 'warning';
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
};

// Query params required by all dashboard endpoints
const dashParams = () => {
  const today = todayStr();
  return { ShippedDate: today, OrdersReviewTodayDate: today };
};

// ─── Dashboard ────────────────────────────────────────────────────────────────

export async function fetchDashboardAlerts() {
  try {
    const [shippingRows, countRows, vipRows, placedRows, onHoldRows] = await Promise.all([
      neonQuery('dashboard_alerts_shipping'),
      neonQuery('dashboard_alerts_counts'),
      neonQuery('dashboard_alerts_vip'),
      neonQuery('orders_placed_today'),
      neonQuery('orders_on_hold_count'),
    ]);
    const a = { ...shippingRows[0], ...countRows[0], ...vipRows[0] };
    const placedCount = Number(placedRows?.[0]?.total_orders ?? 0);
    const placedRev = Number(placedRows?.[0]?.total_amount ?? 0);
    const reshipCount = Number(placedRows?.[0]?.reship_count ?? 0);
    const newOrders = placedCount - reshipCount;
    const onHold = Number(onHoldRows?.[0]?.total ?? 0);
    const totalShipping = Number(a.total_shipping ?? 0);

    return [
      {
        label: 'Orders Placed Today',
        value: newOrders,
        reships: reshipCount,
        type: placedCount > 0 ? 'info' : 'zero',
        sub: [
          { label: 'Revenue', value: fmt(placedRev) },
          { label: 'AOV', value: newOrders > 0 ? fmt(placedRev / newOrders) : '$0.00' },
        ],
      },
      {
        label: 'Orders Shipping Today',
        value: totalShipping,
        type: totalShipping > 0 ? 'info' : 'zero',
        sub: [
          { label: 'Revenue', value: fmt(Number(a.total_shipping_amount ?? 0)) },
          { label: 'Avg Profit', value: fmt(Number(a.avg_profit ?? 0)) },
        ],
      },
      { label: 'Late Delivery', value: Number(a.late_orders ?? 0), type: getAlertType(a.late_orders) },
      { label: 'Orders Processed Today', value: Number(a.total_processed ?? 0), type: getAlertType(a.total_processed) },
      { label: 'Orders Remaining Today', value: Number(a.total_remaining ?? 0), type: getAlertType(a.total_remaining) },
      { label: 'Orders on Hold', value: onHold, type: getAlertType(onHold) },
      { label: 'Orders Needing Charge/Credit (Last 120 Days)', value: Number(a.charge_count ?? 0), type: getAlertType(a.charge_count) },
      { label: 'Open Claim Orders (Last 120 Days)', value: Number(a.claim_count ?? 0), type: getAlertType(a.claim_count) },
      { label: 'Fraud Alerts', value: Number(a.fraud_alerts ?? 0), type: getAlertType(a.fraud_alerts) },
      { label: 'Product Alerts', value: Number(a.product_alerts ?? 0), type: getAlertType(a.product_alerts) },
      { label: 'Unread Contact', value: 0, type: 'zero' },
      { label: 'Physical Gift Certificate Orders', value: Number(a.gift_cert_orders ?? 0), type: Number(a.gift_cert_orders ?? 0) > 0 ? 'info' : 'zero' },
      { label: 'VIP Club Packets', value: 0, type: 'zero' },
      { label: 'VIPs without Profiles', value: Number(a.vip_without_profile ?? 0), type: getAlertType(a.vip_without_profile) },
      { label: 'Order Shipping Errors', value: Number(a.shipping_errors ?? 0), type: Number(a.shipping_errors ?? 0) > 1000 ? 'danger' : getAlertType(a.shipping_errors) },
      { label: 'AK/HI Orders', value: Number(a.ak_hi_orders ?? 0), type: getAlertType(a.ak_hi_orders) },
      { label: 'Shipping Address Validations', value: Number(a.address_validations ?? 0), type: Number(a.address_validations ?? 0) > 500 ? 'danger' : getAlertType(a.address_validations) },
      { label: 'Large Ground Orders', value: Number(a.large_ground_count ?? 0), type: getAlertType(a.large_ground_count) },
    ];
  } catch (_) {
    // Fallback to production API
    const params = dashParams();
    const [alertRes, chargeRes, placedRows, onHoldRows, vipWithoutRes] = await Promise.all([
      get('/Dashboard/GetDashboardAlertInfo', params),
      get('/Dashboard/GetDashboardChargeAndClaimOrderInfo', params),
      neonQuery('orders_placed_today').catch(() => null),
      neonQuery('orders_on_hold_count').catch(() => null),
      get('/Dashboard/VIPWithoutProfile').catch(() => null),
    ]);
    const a = alertRes ?? {};
    const chargeCount = Number(chargeRes?.[0] ?? 0);
    const claimCount = Number(chargeRes?.[1] ?? 0);
    const placedCount = Number(placedRows?.[0]?.total_orders ?? 0);
    const placedRev = Number(placedRows?.[0]?.total_amount ?? 0);
    const reshipCount = Number(placedRows?.[0]?.reship_count ?? 0);
    const newOrders = placedCount - reshipCount;
    const onHold = Number(onHoldRows?.[0]?.total ?? 0);
    return [
      { label: 'Orders Placed Today', value: newOrders, reships: reshipCount, type: placedCount > 0 ? 'info' : 'zero', sub: [{ label: 'Revenue', value: fmt(placedRev) }, { label: 'AOV', value: newOrders > 0 ? fmt(placedRev / newOrders) : '$0.00' }] },
      { label: 'Orders Shipping Today', value: Number(a.alertsOrdersProcessedStatus?.totalShipping ?? 0), type: Number(a.alertsOrdersProcessedStatus?.totalShipping ?? 0) > 0 ? 'info' : 'zero', sub: [{ label: 'Revenue', value: fmt(a.alertsOrdersProcessedStatus?.totalAmount) }, { label: 'Avg Profit', value: fmt(a.alertsOrdersProcessedStatus?.averageProfit) }] },
      { label: 'Late Delivery', value: Number(a.lateOrders ?? 0), type: getAlertType(a.lateOrders) },
      { label: 'Orders Processed Today', value: Number(a.alertsOrdersProcessedStatus?.totalProcessedShipping ?? 0), type: getAlertType(a.alertsOrdersProcessedStatus?.totalProcessedShipping) },
      { label: 'Orders Remaining Today', value: Number(a.alertsOrdersProcessedStatus?.totalRemainingShipping ?? 0), type: getAlertType(a.alertsOrdersProcessedStatus?.totalRemainingShipping) },
      { label: 'Orders on Hold', value: onHold, type: getAlertType(onHold) },
      { label: 'Orders Needing Charge/Credit (Last 120 Days)', value: chargeCount, type: getAlertType(chargeCount) },
      { label: 'Open Claim Orders (Last 120 Days)', value: claimCount, type: getAlertType(claimCount) },
      { label: 'Fraud Alerts', value: Number(a.alertsOrdersFrauds ?? 0), type: getAlertType(a.alertsOrdersFrauds) },
      { label: 'Product Alerts', value: Number(a.alertProducts ?? 0), type: getAlertType(a.alertProducts) },
      { label: 'Unread Contact', value: Number(a.alertsUnreadContacts ?? 0), type: getAlertType(a.alertsUnreadContacts) },
      { label: 'Physical Gift Certificate Orders', value: Number(a.alertsGifts ?? 0), type: Number(a.alertsGifts ?? 0) > 0 ? 'info' : 'zero' },
      { label: 'VIP Club Packets', value: Number(a.vipPacketsToSend ?? 0), type: Number(a.vipPacketsToSend ?? 0) > 0 ? 'info' : 'zero' },
      { label: 'VIPs without Profiles', value: Number(vipWithoutRes ?? 0), type: getAlertType(vipWithoutRes) },
      { label: 'Order Shipping Errors', value: Number(a.shippingOrderErrors ?? 0), type: Number(a.shippingOrderErrors ?? 0) > 1000 ? 'danger' : getAlertType(a.shippingOrderErrors) },
      { label: 'AK/HI Orders', value: Number(a.aHAKOrderCount ?? 0), type: getAlertType(a.aHAKOrderCount) },
      { label: 'Shipping Address Validations', value: Number(a.shippingAdressValidation ?? 0), type: Number(a.shippingAdressValidation ?? 0) > 500 ? 'danger' : getAlertType(a.shippingAdressValidation) },
      { label: 'Large Ground Orders', value: Number(a.largeGroundCount ?? 0), type: getAlertType(a.largeGroundCount) },
    ];
  }
}

// Single-shot dashboard payload from the precomputed snapshot table
// (refreshed every 15 min by dashboard-snapshot-refresh.js). Replaces 5+
// parallel Neon fetches on dashboard mount. Returns null if the snapshot
// hasn't been populated yet — caller should fall back to live fetches.
export async function fetchDashboardSnapshot() {
  try {
    const rows = await neonQuery('dashboard_snapshot_get');
    if (!rows || !rows.length) return null;
    return { ...rows[0].payload, refreshedAt: rows[0].refreshed_at };
  } catch (_) {
    return null;
  }
}

export async function fetchDashboardKpis() {
  // Hit the same production endpoint the live admin uses
  // (Dashboard/GetDashboardSalesAndProfitInfo → usp_Select_Dashboard_Orders_ProfitAndSales).
  // Previously this read from the Neon profit-table mirror, but its sync is incomplete
  // (missing days + never writes net_profit_re_ship), so order/profit/avg-profit drift
  // 4-5x off the live admin. Sales matched within ~1%; profit/orders/avgProfit didn't.
  // _host=bak — Dashboard controllers are only deployed on api-bak.crabplace.com,
  // not api-dev (same as VIPRenewal). api-proxy strips this param before forwarding.
  const res = await fetch('/api/Dashboard/GetDashboardSalesAndProfitInfo?_host=bak', { headers: authHeaders() });
  if (!res.ok) throw new Error(`Dashboard KPIs: ${res.status}`);
  const arr = await res.json();
  // Response shape is an array of two anonymous objects from the C# controller:
  //   [{ ordersProfitAndSalesOfThisYear: {...} }, { ordersProfitAndSalesOfLastYear: {...} }]
  // .NET defaults to camelCase property names, but read both casings defensively.
  const pick = (entry, baseKey) => entry?.[baseKey] ?? entry?.[baseKey[0].toUpperCase() + baseKey.slice(1)] ?? {};
  const cur = pick(arr?.[0] ?? {}, 'ordersProfitAndSalesOfThisYear');
  const prev = pick(arr?.[1] ?? {}, 'ordersProfitAndSalesOfLastYear');
  const num = (obj, ...keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null) return Number(v) || 0;
    }
    return 0;
  };
  // SP field map (mapped to C# VM PascalCase or JSON camelCase):
  //   coid → totalOrder/TotalOrder         — order count
  //   sprof → totalProfit/TotalProfit      — net profit (new + reship)
  //   soamt → totalOrderAmount/TotalOrderAmount — sales from Orders table
  //   aprof → quotientProfit/QuotientProfit — avg profit / order
  //   GrossTotal sum → totalAmount/TotalAmount — for margin denominator
  const curSales    = num(cur, 'totalOrderAmount', 'TotalOrderAmount');
  const prevSales   = num(prev, 'totalOrderAmount', 'TotalOrderAmount');
  const curProfit   = num(cur, 'totalProfit', 'TotalProfit');
  const prevProfit  = num(prev, 'totalProfit', 'TotalProfit');
  const curOrders   = num(cur, 'totalOrder', 'TotalOrder');
  const prevOrders  = num(prev, 'totalOrder', 'TotalOrder');
  const curGross    = num(cur, 'totalAmount', 'TotalAmount') || curSales;
  const prevGross   = num(prev, 'totalAmount', 'TotalAmount') || prevSales;
  const curMargin   = curGross ? (curProfit / curGross * 100) : 0;
  const prevMargin  = prevGross ? (prevProfit / prevGross * 100) : 0;
  const curAvgProfit  = num(cur, 'quotientProfit', 'QuotientProfit') || (curOrders ? curProfit / curOrders : 0);
  const prevAvgProfit = num(prev, 'quotientProfit', 'QuotientProfit') || (prevOrders ? prevProfit / prevOrders : 0);

  return {
    sales:     { value: fmt(curSales),     compare: `${fmt(prevSales)} last year`, trend: curSales     >= prevSales     ? 'up' : 'down' },
    profit:    { value: fmt(curProfit),    compare: `(${fmt(prevProfit)} last year)`, margin: pct(curMargin), lastYearMargin: pct(prevMargin), trend: curProfit  >= prevProfit    ? 'up' : 'down' },
    orders:    { value: String(curOrders), compare: `${prevOrders} last year`,      trend: curOrders    >= prevOrders    ? 'up' : 'down' },
    avgProfit: { value: fmt(curAvgProfit), compare: `${fmt(prevAvgProfit)} last year`, trend: curAvgProfit >= prevAvgProfit ? 'up' : 'down' },
  };
}

// Helper: get Sun–Sat date range for a given week offset
function getWeekRange(weeksBack = 0) {
  // Use ET date to avoid UTC timezone shift issues
  const todayET = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD
  const refDate = new Date(todayET + 'T12:00:00'); // noon to avoid DST edge cases
  const dayOfWeek = refDate.getDay();
  const daysToSat = dayOfWeek === 6 ? 0 : 6 - dayOfWeek;
  const sat = new Date(refDate.getTime() + (daysToSat - weeksBack * 7) * 86400000);
  const sun = new Date(sat.getTime() - 6 * 86400000);
  const toISO = d => d.toISOString().slice(0, 10);
  return { start: toISO(sun), end: toISO(sat), year: sat.getFullYear() };
}

// Map Neon rows to day-of-week lookup (using UTC day)
function rowsByDay(rows, startDate) {
  const byDay = {};
  if (startDate) {
    // Map by position within the range (day 0 = startDate, day 1 = startDate+1, etc.)
    const startMs = new Date(startDate + 'T00:00:00Z').getTime();
    rows.forEach(r => {
      const d = new Date(r.ship_date);
      const dayIdx = Math.round((d.getTime() - startMs) / 86400000);
      if (dayIdx >= 0 && dayIdx < 7) byDay[dayIdx] = r;
    });
  } else {
    rows.forEach(r => {
      const d = new Date(r.ship_date);
      byDay[d.getUTCDay()] = r;
    });
  }
  return byDay;
}

// When the order sync last finished (ISO time string, or null). Shown under the date on the
// phone dashboard so a stale count is visible.
export async function fetchLastOrderSync() {
  const d = await neonQuery('last_order_sync');
  return d && d.finishedAt ? d.finishedAt : null;
}

export async function fetchWeeklyOrders(weeksBack = 0) {
  const thisWeek = getWeekRange(weeksBack);
  // Match production: swap year on Saturday, find that week's Sun–Sat
  const lastYearSatStr = thisWeek.end.replace(/^\d{4}/, String(thisWeek.year - 1));
  const lastYearSat = new Date(lastYearSatStr + 'T12:00:00');
  // Walk to the nearest Saturday (in case the swapped date lands on a different day)
  const satDay = lastYearSat.getDay(); // 0=Sun..6=Sat
  const daysToSat = satDay === 6 ? 0 : (6 - satDay);
  lastYearSat.setDate(lastYearSat.getDate() + daysToSat);
  const lastYearSun = new Date(lastYearSat.getTime() - 6 * 86400000);
  const toISO = d => d.toISOString().slice(0, 10);
  const lastYearStart = toISO(lastYearSun);
  const lastYearEnd = toISO(lastYearSat);

  // Neon only — no API fallback
  const [thisRows, lastRows] = await Promise.all([
      neonQuery('weekly_orders', { startDate: thisWeek.start, endDate: thisWeek.end }),
      neonQuery('weekly_orders', { startDate: lastYearStart, endDate: lastYearEnd }),
    ]);
    const byDayThis = rowsByDay(thisRows, thisWeek.start);
    const byDayLast = rowsByDay(lastRows, lastYearStart);
    return DAYS.map((day, i) => {
      const t = byDayThis[i] ?? {};
      const l = byDayLast[i] ?? {};
      return {
        day,
        orders2025: Number(l.total_order ?? 0),
        orders2026: Number(t.total_order ?? 0),
        rev2025: fmt(Number(l.total_order_amount ?? 0)),
        rev2026: fmt(Number(t.total_order_amount ?? 0)),
        profit2026: '$0.00',
      };
    });
}

// Hourly orders for today vs same weekday 364 days back (DOW-aligned).
// Returns 24 rows: [{hour: 0..23, orders2026, orders2025, rev2026, rev2025}, ...]
export async function fetchHourlyOrders() {
  const toLocalISO = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  };
  const today = new Date();
  const yearAgo = new Date(today.getTime() - 364 * 86400000);
  const todayDate = toLocalISO(today);
  const yearAgoDate = toLocalISO(yearAgo);

  const [thisRows, lastRows] = await Promise.all([
    neonQuery('hourly_orders', { date: todayDate }),
    neonQuery('hourly_orders', { date: yearAgoDate }),
  ]);
  const byHour = (rows) => {
    const m = {};
    (rows || []).forEach(r => { m[Number(r.hour)] = r; });
    return m;
  };
  const t = byHour(thisRows);
  const l = byHour(lastRows);
  return Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    orders2026: Number(t[h]?.total_order ?? 0),
    orders2025: Number(l[h]?.total_order ?? 0),
    rev2026: Number(t[h]?.total_order_amount ?? 0),
    rev2025: Number(l[h]?.total_order_amount ?? 0),
  }));
}

export async function fetchWeeklyOrdersForYear(year) {
  const thisWeek = getWeekRange(0);
  // Match production: swap year on Saturday, find that week's Sun–Sat
  const swappedSat = new Date(thisWeek.end.replace(/^\d{4}/, String(year)) + 'T12:00:00');
  const sd = swappedSat.getDay();
  swappedSat.setDate(swappedSat.getDate() + (sd === 6 ? 0 : 6 - sd));
  const swappedSun = new Date(swappedSat.getTime() - 6 * 86400000);
  const toISO2 = d => d.toISOString().slice(0, 10);
  const startDate = toISO2(swappedSun);
  const endDate = toISO2(swappedSat);
  // Neon only
  const rows = await neonQuery('weekly_orders', { startDate, endDate });
  const byDay = rowsByDay(rows, startDate);
  return DAYS.map((day, i) => ({
    day,
    orders: Number(byDay[i]?.total_order ?? 0),
    rev: fmt(Number(byDay[i]?.total_order_amount ?? 0)),
  }));
}

export async function fetchCategoryData() {
  const week = getWeekRange(0);
  const lastYearStart = week.start.replace(/^\d{4}/, String(week.year - 1));
  const lastYearEnd = week.end.replace(/^\d{4}/, String(week.year - 1));

  try {
    const [thisYear, lastYear] = await Promise.all([
      neonQuery('dashboard_category_sales', { startDate: week.start, endDate: week.end }),
      neonQuery('dashboard_category_sales', { startDate: lastYearStart, endDate: lastYearEnd }),
    ]);
    const lastByName = {};
    lastYear.forEach(c => { lastByName[c.category_name] = c; });
    const thisByName = {};
    thisYear.forEach(c => { thisByName[c.category_name] = c; });
    const allNames = [...new Set([...lastYear, ...thisYear].map(c => c.category_name))];
    return allNames.map(name => ({
      name,
      orders2025: Number(lastByName[name]?.total_order ?? 0),
      orders2026: Number(thisByName[name]?.total_order ?? 0),
      sales2025: fmt(Number(lastByName[name]?.total_amount ?? 0)),
      sales2026: fmt(Number(thisByName[name]?.total_amount ?? 0)),
    }));
  } catch (_) {}
  // Fallback
  const refDate = new Date();
  const dayOfWeek = refDate.getDay();
  const daysBack = dayOfWeek === 6 ? 0 : dayOfWeek + 1;
  const sat = new Date(refDate.getTime() - daysBack * 86400000);
  const mm = String(sat.getMonth() + 1).padStart(2, '0');
  const dd = String(sat.getDate()).padStart(2, '0');
  const satStr = `${mm}/${dd}/${sat.getFullYear()}`;
  const res = await get('/Dashboard/GetDashboardSalesByCategory', { ShippedDate: satStr, OrdersReviewTodayDate: satStr });
  const lastYear2 = res?.[0]?.salesLastYear ?? [];
  const thisYear2 = res?.[2]?.salesThisYear ?? res?.[1]?.salesThisYear ?? [];
  const lastByName = {};
  lastYear2.forEach(c => { lastByName[c.categoryName] = c; });
  const thisByName = {};
  thisYear2.forEach(c => { thisByName[c.categoryName] = c; });
  const allNames = [...new Set([...lastYear2, ...thisYear2].map(c => c.categoryName))];
  return allNames.map(name => ({
    name,
    orders2025: Number(lastByName[name]?.totalOrders ?? 0),
    orders2026: Number(thisByName[name]?.totalOrders ?? 0),
    sales2025: fmt(lastByName[name]?.totalSales),
    sales2026: fmt(thisByName[name]?.totalSales),
  }));
}

// ── Category Sales Report ─────────────────────────────────────────────────────
//
// Fetches per-category aggregate + time-series data for both a current period
// and a comparison period (YoY or MoM). Result shape is rendered by the
// CategorySalesReport page; do not change without checking that consumer.
//
// granularity: 'month' | 'week' | 'day' — drives both bucket format on the
// server and how the sparkline x-axis is interpreted on the client.
const BUCKET_FMT = {
  month: 'YYYY-MM',
  week: 'IYYY-"W"IW',
  day: 'YYYY-MM-DD',
};

export async function fetchCategorySalesReport({ current, prior, granularity = 'month' }) {
  const bucketFmt = BUCKET_FMT[granularity] ?? BUCKET_FMT.month;

  // Fetch in parallel:
  // - category_sales_timeseries (oitems-summed, per-category breakdown)
  // - weekly_orders (orderamount-summed, the canonical period totals — these
  //   are what the dashboard headline tile shows; oitems sums slightly higher
  //   because line-item totals don't account for order-level discounts/coupons)
  const [curRows, priorRows, curWeekly, priorWeekly] = await Promise.all([
    neonQuery('category_sales_timeseries', { startDate: current.start, endDate: current.end, bucketFmt }),
    neonQuery('category_sales_timeseries', { startDate: prior.start, endDate: prior.end, bucketFmt }),
    neonQuery('weekly_orders', { startDate: current.start, endDate: current.end }),
    neonQuery('weekly_orders', { startDate: prior.start, endDate: prior.end }),
  ]);

  // Aggregate per category across all buckets, plus keep the bucketed series.
  const aggregate = (rows) => {
    const byCat = {};
    rows.forEach((r) => {
      const name = r.category_name;
      const rev = Number(r.revenue ?? 0);
      const ord = Number(r.orders ?? 0);
      if (!byCat[name]) byCat[name] = { revenue: 0, orders: 0, buckets: {} };
      byCat[name].revenue += rev;
      byCat[name].orders += ord;
      byCat[name].buckets[r.bucket] = { revenue: rev, orders: ord };
    });
    return byCat;
  };

  const cur = aggregate(curRows);
  const pri = aggregate(priorRows);

  const allNames = [...new Set([...Object.keys(cur), ...Object.keys(pri)])];
  const lineTotalCur = Object.values(cur).reduce((s, c) => s + c.revenue, 0);
  const lineTotalPrior = Object.values(pri).reduce((s, c) => s + c.revenue, 0);

  const categories = allNames.map((name) => {
    const c = cur[name] ?? { revenue: 0, orders: 0, buckets: {} };
    const p = pri[name] ?? { revenue: 0, orders: 0, buckets: {} };
    const deltaRev = c.revenue - p.revenue;
    const deltaPct = p.revenue > 0 ? (deltaRev / p.revenue) * 100 : (c.revenue > 0 ? null : 0); // null = brand new (no prior)
    const deltaOrders = c.orders - p.orders;
    const deltaOrdersPct = p.orders > 0 ? (deltaOrders / p.orders) * 100 : (c.orders > 0 ? null : 0);
    return {
      name,
      current: { revenue: c.revenue, orders: c.orders, buckets: c.buckets },
      prior: { revenue: p.revenue, orders: p.orders, buckets: p.buckets },
      deltaRev,
      deltaPct,
      deltaOrders,
      deltaOrdersPct,
      shareOfMix: lineTotalCur > 0 ? (c.revenue / lineTotalCur) * 100 : 0,
    };
  });

  // Canonical totals from orders.orderamount (these match the dashboard's headline tile).
  const orderRevCur = curWeekly.reduce((s, r) => s + Number(r.total_order_amount ?? 0), 0);
  const orderRevPrior = priorWeekly.reduce((s, r) => s + Number(r.total_order_amount ?? 0), 0);
  const orderCntCur = curWeekly.reduce((s, r) => s + Number(r.total_order ?? 0), 0);
  const orderCntPrior = priorWeekly.reduce((s, r) => s + Number(r.total_order ?? 0), 0);

  return {
    categories,
    totals: {
      // Dashboard-canonical (orderamount summed, strict filters)
      currentRevenue: orderRevCur,
      priorRevenue: orderRevPrior,
      currentOrders: orderCntCur,
      priorOrders: orderCntPrior,
      revenueDeltaPct: orderRevPrior > 0 ? ((orderRevCur - orderRevPrior) / orderRevPrior) * 100 : null,
      ordersDeltaPct: orderCntPrior > 0 ? ((orderCntCur - orderCntPrior) / orderCntPrior) * 100 : null,
      // Line-item totals (sum across categories — slightly higher because they
      // don't reflect order-level discounts/shipping/coupon adjustments).
      currentLineItemRevenue: lineTotalCur,
      priorLineItemRevenue: lineTotalPrior,
    },
  };
}

export async function fetchCategorySalesProducts({ categoryName, current, prior }) {
  const [curRows, priorRows] = await Promise.all([
    neonQuery('category_sales_products', { startDate: current.start, endDate: current.end, categoryName }),
    neonQuery('category_sales_products', { startDate: prior.start, endDate: prior.end, categoryName }),
  ]);
  // Match products across years on catalogid (stable across BC migration);
  // legacy rows without a catalogid (rare) fall back to the product_name key.
  const keyOf = (r) => (r.catalogid && r.catalogid !== '') ? `cat:${r.catalogid}` : `name:${r.product_name}`;
  const priorByKey = {};
  priorRows.forEach((r) => { priorByKey[keyOf(r)] = { revenue: Number(r.revenue ?? 0), orders: Number(r.orders ?? 0), units: Number(r.units ?? 0), name: r.product_name }; });
  const curByKey = {};
  curRows.forEach((r) => { curByKey[keyOf(r)] = { revenue: Number(r.revenue ?? 0), orders: Number(r.orders ?? 0), units: Number(r.units ?? 0), name: r.product_name }; });
  const allKeys = [...new Set([...Object.keys(curByKey), ...Object.keys(priorByKey)])];
  return allKeys.map((key) => {
    const c = curByKey[key] ?? { revenue: 0, orders: 0, units: 0, name: priorByKey[key]?.name };
    const p = priorByKey[key] ?? { revenue: 0, orders: 0, units: 0, name: curByKey[key]?.name };
    // Prefer the modern (current) name when both exist; modern is the post-BC
    // catalog name, which is what admins see in the live BC admin UI.
    const name = curByKey[key]?.name ?? priorByKey[key]?.name ?? '(unknown)';
    const deltaPct = p.revenue > 0 ? ((c.revenue - p.revenue) / p.revenue) * 100 : (c.revenue > 0 ? null : 0);
    return { name, current: c, prior: p, deltaRev: c.revenue - p.revenue, deltaPct };
  }).sort((a, b) => b.current.revenue - a.current.revenue);
}

export async function fetchDeliveryRecon() {
  try {
    const week = getWeekRange(0);
    const rows = await neonQuery('dashboard_delivery_recon', { startDate: week.start, endDate: week.end });
    const d = rows[0] ?? {};
    return {
      period: `${week.start} - ${week.end}`,
      onTime: Number(d.delivered ?? 0),
      late: 0,
      inTrouble: Number(d.exceptions ?? 0),
      lateOrderTotal: 0,
      avgLateTime: '0 days',
    };
  } catch (_) {}
  const data = await get('/Dashboard/GetDashboardDeliveryReconInfo', dashParams());
  const d = data?.[0] ?? {};
  const days = d.avgLateHours ? Math.round(d.avgLateHours / 24) : 0;
  return {
    period: d.startDate && d.endDate ? `${d.startDate} - ${d.endDate}` : '',
    onTime: Number(d.onTimeCount ?? 0),
    late: Number(d.lateCount ?? 0),
    inTrouble: Number(d.inTroubleCount ?? 0),
    lateOrderTotal: Number(d.lateOrderTotal ?? 0),
    avgLateTime: `${days} day${days !== 1 ? 's' : ''}`,
  };
}

export async function fetchFeedbackData() {
  try {
    const rows = await neonQuery('dashboard_feedback');
    const r = rows[0] ?? {};
    return {
      count: Number(r.feedback_count ?? 0),
      // The send day as text, so nothing has to re-parse the display date (new Date('YYYY-MM-DD') is UTC and lands a day early in ET).
      ymd: /^\d{4}-\d{2}-\d{2}/.test(String(r.feed_back_date || '')) ? String(r.feed_back_date).slice(0, 10) : '',
      date: r.feed_back_date ? ((d) => `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`)(new Date(r.feed_back_date)) : '',
      scores: [
        { label: 'Product', score: Number(r.prod_rate ?? 0).toFixed(2) },
        { label: 'Packaging', score: Number(r.pack_rate ?? 0).toFixed(1) },
        { label: 'Delivery', score: Number(r.deliv_rate ?? 0).toFixed(1) },
        { label: 'Customer Service', score: Number(r.serv_rate ?? 0).toFixed(1) },
        { label: 'Ease', score: Number(r.ease_rate ?? 0).toFixed(1) },
        { label: 'Order Again', score: Number(r.like_rate ?? 0).toFixed(1) },
        { label: 'Overall', score: Number(r.overall_rate ?? 0).toFixed(1) },
      ],
    };
  } catch (_) {}
  const data = await get('/Dashboard/GetDashboardFeedbackInfo', dashParams());
  const raw = data?.[0]?.feedback ?? data?.[0] ?? {};
  return {
    count: Number(raw.feedbackCount ?? 0),
    date: raw.feedBackDate ? ((d) => `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`)(new Date(raw.feedBackDate)) : '',
    scores: [
      { label: 'Product', score: Number(raw.prodRate ?? 0).toFixed(2) },
      { label: 'Packaging', score: Number(raw.packRate ?? 0).toFixed(1) },
      { label: 'Delivery', score: Number(raw.delivRate ?? 0).toFixed(1) },
      { label: 'Customer Service', score: Number(raw.servRate ?? 0).toFixed(1) },
      { label: 'Ease', score: Number(raw.easeRate ?? 0).toFixed(1) },
      { label: 'Order Again', score: Number(raw.likeRate ?? 0).toFixed(1) },
      { label: 'Overall', score: Number(raw.overallRate ?? 0).toFixed(1) },
    ],
  };
}

export async function fetchVipData() {
  try {
    const rows = await neonQuery('dashboard_vip');
    const v = rows[0] ?? {};
    const activeMembers = Number(v.active_members ?? 0);
    const renewalCount = Number(v.renewal_count ?? 0);
    return {
      goal2026: 20000,
      current: activeMembers,
      week: {
        newVips: Number(v.week_new_vips ?? 0),
        reactivated: Number(v.week_reactivated ?? 0),
        totalVipOrders: Number(v.week_orders ?? 0),
        newVipRevenue: fmt(v.week_new_revenue),
        existingVipRevenue: fmt(v.week_existing_revenue),
        totalVipRevenue: fmt(v.week_total_revenue),
      },
      month: {
        newVips: Number(v.month_new_vips ?? 0),
        reactivated: Number(v.month_reactivated ?? 0),
        totalVipOrders: Number(v.month_orders ?? 0),
        newVipRevenue: fmt(v.month_new_revenue),
        existingVipRevenue: fmt(v.month_existing_revenue),
        totalVipRevenue: fmt(v.month_total_revenue),
      },
      activePurchases: [
        { label: '<90 Days', value: Number(v.active_vip_90 ?? 0) },
        { label: '91-180 Days', value: Number(v.active_vip_180 ?? 0) - Number(v.active_vip_90 ?? 0) },
        { label: '181-270 Days', value: Number(v.active_vip_270 ?? 0) - Number(v.active_vip_180 ?? 0) },
        { label: '271-365 Days', value: Number(v.active_vip_365 ?? 0) - Number(v.active_vip_270 ?? 0) },
        { label: '365+ Days', value: Number(v.active_vip_365plus ?? 0) - Number(v.active_vip_365 ?? 0) },
      ],
      activeRenewals: renewalCount,
      activeMembers,
      renewalAmount: fmt(v.renewal_amount),
      renewalSchedule: (Array.isArray(v.renewal_months) ? v.renewal_months : []).map(m => ({
        label: m.monthLabel,
        members: Number(m.members ?? 0),
        amount: fmt(m.amount),
      })),
    };
  } catch (_) {}
  const timeout = (ms) => new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms));
  const [data, clubInfo] = await Promise.all([
    Promise.race([get('/Dashboard/GetDashboardVIPClubInfo', dashParams()), timeout(10000)]).catch(() => null),
    get('/Dashboard/GetVIPClubInfo').catch(() => null),
  ]);
  const activeMembers = Number(data?.[0]?.vipActiveMembers ?? 0);
  const renewals = data?.[1]?.vipRenewals ?? {};
  const subs = (Array.isArray(data) ? data.find(d => d?.rechargeSubscriptions) : null)?.rechargeSubscriptions ?? {};
  const c = clubInfo ?? {};
  return {
    goal2026: 20000,
    current: Number(subs.active ?? activeMembers),
    week: {
      newVips: Number(c.weekTotalNewVip ?? 0),
      reactivated: Number(c.weekTotalReactivatedVIP ?? 0),
      totalVipOrders: Number(c.weekTotalVIPOrders ?? 0),
      newVipRevenue: fmt(c.weekTotalNewVipRevenue),
      existingVipRevenue: fmt(c.weekTotalExistingVipRevenue),
      totalVipRevenue: fmt(c.weekTotalVIPRevenue),
    },
    month: {
      newVips: Number(c.monthTotalNewVip ?? 0),
      reactivated: Number(c.monthTotalReactivatedVIP ?? 0),
      totalVipOrders: Number(c.monthTotalVIPOrders ?? 0),
      newVipRevenue: fmt(c.monthTotalNewVipRevenue),
      existingVipRevenue: fmt(c.monthTotalExistingVipRevenue),
      totalVipRevenue: fmt(c.monthTotalVIPRevenue),
    },
    activePurchases: [],
    activeRenewals: Number(subs.willRenew ?? 0),
    activeMembers,
    renewalAmount: fmt(renewals.cAmt),
    renewalSchedule: (Array.isArray(renewals.months) ? renewals.months : []).map(m => ({
      label: m.monthLabel,
      members: Number(m.members ?? 0),
      amount: fmt(m.amount),
    })),
  };
}

// New VIPs today / this week. today + week are BOTH routes added together — a
// membership bought at checkout, or one a CS rep activated by hand after an
// outreach email (which leaves no order line, so the order query alone misses it).
// The splits ride along so the tile can show the same "N checkout · N CS wins"
// breakdown the TV board shows.
export async function fetchVipSignups() {
  const rows = await neonQuery('vip_signups_periods');
  const r = rows?.[0] ?? {};
  return {
    today: Number(r.today_count ?? 0),
    todayCheckout: Number(r.today_checkout ?? 0),
    todayCs: Number(r.today_cs ?? 0),
    week: Number(r.week_count ?? 0),
    weekCheckout: Number(r.week_checkout ?? 0),
    weekCs: Number(r.week_cs ?? 0),
  };
}

// The VIP block exactly as the condo TV board has it: renewing / not renewing /
// active, the 7-day net-change trend, and new VIPs split checkout vs CS win.
//
// ⚠️ Deliberately the BOARD'S OWN ENDPOINT rather than a second set of queries.
// kiosk-dashboard already takes a live CPx staff session as one of its two doors
// (lib/kiosk-auth), so asking it directly is what guarantees the wall and the
// desktop can never print different numbers — the thing that was wrong on
// 2026-08-26, when only the wall counted CS-won VIPs.
//
// A 401 here throws rather than logging the user out the way neonQuery does: this
// is one card on a page full of others, and a trend chart is not worth ending
// somebody's session over.
export async function fetchVipBoard() {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/kiosk-dashboard`, { headers });
  if (!res.ok) throw new Error(res.status === 401 ? 'Not authorized for the VIP board' : `VIP board error: ${res.status}`);
  const data = await res.json();
  return data.vip || null;
}

// ── Neon row → common order row shape used by list pages ──────────────────────

function neonToOrderRow(r) {
  return {
    bcOrderId: r.bc_order_id,
    orderId: r.id,
    billTo: [r.first_name, r.last_name].filter(Boolean).join(' ') || '—',
    firstName: r.first_name,
    phone: r.customer_phone,
    shipTo: [r.ship_first_name, r.ship_last_name].filter(Boolean).join(' ') || undefined,
    shipType: r.ship_method ?? '—',
    orderTotal: Number(r.order_amount ?? 0),
    orderDate: r.order_date,
    email: r.email,
    fraudScore: Number(r.fraud_score ?? 0),
    trackingNumber: r.tracking_number,
    status: r.status,
    // Extra fields for specific list pages (present only when queried)
    ...(r.ship_date != null && { shipDate: r.ship_date }),
    ...(r.done != null && { done: r.done }),
    ...(r.balance != null && { balance: Number(r.balance), claimBalance: Number(r.balance) }),
    ...(r.ship_state != null && { state: r.ship_state, shipState: r.ship_state }),
    ...(r.shipping_review != null && { shippingReview: r.shipping_review && r.shipping_review !== '0' && r.shipping_review !== '' }),
    ...(r.large_ground_review != null && { largeGroundReview: r.large_ground_review && r.large_ground_review !== '0' && r.large_ground_review !== '' }),
    ...(r.ship_address != null && { shipAddress: r.ship_address }),
    ...(r.ship_city != null && { shipCity: r.ship_city }),
    ...(r.ship_state_addr != null && { shipState: r.ship_state_addr, state: r.ship_state_addr }),
    ...(r.ship_zip != null && { shipZip: r.ship_zip }),
    ...(r.shipping_review != null && { validationReviewed: r.shipping_review && r.shipping_review !== '0' && r.shipping_review !== '' }),
    ...(r.prod_alert != null && { problemType: r.prod_alert, prodAlert: r.prod_alert }),
    ...(r.original_order_id != null && { originalOrderId: r.original_order_id && r.original_order_id !== '' && r.original_order_id !== '0' ? r.original_order_id : null }),
    ...(r.delivery_date != null && { deliveryDate: r.delivery_date }),
    ...(r.delivery_exception != null && { deliveryException: r.delivery_exception }),
    ...(r.estimated_delivery_date != null && { estimatedDeliveryDate: r.estimated_delivery_date }),
    ...(r.actual_delivery_date != null && { actualDeliveryDate: r.actual_delivery_date }),
    ...(r.ship_first_name != null && { recipientName: [r.ship_first_name, r.ship_last_name].filter(Boolean).join(' ') }),
  };
}

// ─── Shipping Today ───────────────────────────────────────────────────────────

export async function fetchPackingSlips(internalIds) {
  const results = await Promise.all(internalIds.map(async id => {
    const [orderRows, itemRows] = await Promise.all([
      neonQuery('order_by_id', { orderId: id }),
      neonQuery('order_items', { orderId: id }),
    ]);
    const o = orderRows?.[0];
    if (!o) return null;
    return {
      orderInfo: {
        orderId: o.id,
        bcOrderId: o.bc_order_id,
        orderDate: o.order_date ? ((d) => { const h = d.getHours() % 12 || 12; const min = String(d.getMinutes()).padStart(2, '0'); const ampm = d.getHours() >= 12 ? 'PM' : 'AM'; return `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)} ${h}:${min}${ampm}`; })(new Date(o.order_date)) : '',
        deliveryDate: o.delivery_date ? ((d) => `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`)(new Date(o.delivery_date)) : '',
        officeNotes: o.office_notes ?? o.office_note ?? '',
        packingComment: o.packing_comment ?? o.packing_notes ?? '',
        giftMessage: o.gift_message ?? '',
        shipMethod: o.ship_method ?? '',
        shipCost: Number(o.ship_cost ?? 0),
        orderTotal: Number(o.order_amount ?? 0),
      },
      customerInfo: {
        firstName: o.bill_first ?? o.first_name ?? '',
        lastName: o.bill_last ?? o.last_name ?? '',
        address: o.bill_addr1 ?? '',
        address2: o.bill_addr2 ?? '',
        city: o.bill_city ?? '',
        state: o.bill_state ?? '',
        postCode: o.bill_zip ?? '',
        phone: o.bill_phone ?? o.phone ?? '',
        customerEmail: o.email ?? '',
        shipName: o.ship_first ?? '',
        shipLastName: o.ship_last ?? '',
        shipAddress: o.ship_addr1 ?? '',
        shipAddress2: o.ship_addr2 ?? '',
        shipTown: o.ship_city ?? '',
        shipState: o.ship_state ?? '',
        shipZip: o.ship_zip ?? '',
      },
      products: (itemRows ?? []).map(i => ({
        name: i.name ?? '',
        sku: i.sku ?? '',
        numItems: i.quantity ?? i.qty ?? 1,
        unitPrice: i.unit_price ?? i.price ?? 0,
        features: Array.isArray(i.options) ? i.options.filter(Boolean).map(opt => ({ feature: opt.feature, value: opt.value })) : [],
      })),
    };
  }));
  return results.filter(Boolean);
}

// Shape production's GetShippingReport payload into the list the page renders.
// Real `done` + `profit` + `isCostsUpdated` come straight from production.
function shapeShippingReport(payload) {
  const rows = payload?.records?.data ?? [];
  return {
    header: payload?.header ?? null,
    orders: rows.map(r => ({
      id: String(r.bcOrderId && r.bcOrderId !== 0 ? r.bcOrderId : r.orderId),
      internalId: String(r.orderId),
      name: (r.billTo ?? '').trim() || '—',
      shipType: r.shipType ?? '',
      total: `$${Number(r.orderTotal ?? 0).toFixed(2)}`,
      profit: Number(r.profit ?? 0),
      done: !!r.done,
      costsSaved: !!r.isCostsUpdated,
      printed: !!r.printed,
      isVip: false, // GetShippingReport doesn't carry VIP status
      reship: r.reshippedId ? String(r.reshippedId) : undefined,
    })),
  };
}

// Fire-and-forget: ask the background function to refresh a date's cache.
function triggerShippingRefresh(date) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  fetch(`${base}/shipping-report-refresh-background`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date }),
  }).catch(() => {});
}

// Production's shipping report has no VIP flag; overlay it from Neon (VIP
// membership isn't time-sensitive, so the mirror is fine). Keyed by internal
// + BC order id. Failures leave isVip=false rather than blocking the list.
async function buildShippingResult(payload, dMMDDYYYY) {
  const shaped = shapeShippingReport(payload);
  try {
    const [mm, dd, yyyy] = String(dMMDDYYYY).split('/');
    const rows = await neonQuery('vip_by_ship_date', { date: `${yyyy}-${mm}-${dd}` });
    const vip = {};
    const noRenew = {};
    (rows || []).forEach(r => {
      const v = Number(r.is_vip) > 0;
      const nr = Number(r.vip_not_renewing) > 0;
      if (r.orderid != null) { vip[String(r.orderid)] = v; noRenew[String(r.orderid)] = nr; }
      if (r.bc_order_id) {
        vip[String(r.bc_order_id)] = v || !!vip[String(r.bc_order_id)];
        noRenew[String(r.bc_order_id)] = nr || !!noRenew[String(r.bc_order_id)];
      }
    });
    shaped.orders.forEach(o => {
      o.isVip = !!(vip[o.internalId] || vip[o.id]);
      o.vipNotRenewing = !!(noRenew[o.internalId] || noRenew[o.id]);
    });
  } catch { /* leave isVip=false */ }
  return shaped;
}

// Who has packed what so far today, for the strip at the top of Shipping Today
// (Carman, 2026-09-02: "so the floor can see where each person is for the day at
// a glance").
//
// Same endpoint and therefore the same numbers as the packing-floor TV and the
// Fulfillment TAKT page — this must never be re-derived here, or the wall and
// the screen start disagreeing about who is ahead.
//
// ⚠️ `date` is only sent when it is NOT today. A caller-supplied date is
// session-gated server-side (backfilling an arbitrary past day is the one shape
// that can put real load on production on demand), whereas the default
// today-path is open. Omitting it keeps the everyday case working even if the
// session is not accepted for any reason.
//
// `date` is YYYY-MM-DD. Returns { available, packedTotal, attributed,
// unattributed, packers[] } — or null if the call fails, because this strip is
// a convenience and must never take the shipping queue down with it.
export async function fetchPackerDay(dateIso) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const todayEt = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const body = { action: 'status' };
  if (dateIso && dateIso !== todayEt) body.date = dateIso;
  try {
    const res = await fetch(`${base}/packer-attribution`, {
      method: 'POST', headers, body: JSON.stringify(body),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.error) return null;
    return d;
  } catch (_) {
    return null;
  }
}

export async function fetchShippingToday(date) {
  // Reads the CPx cache of production's GetShippingReport (real done/profit),
  // refreshed by a background function. Production's TODAY report takes ~20s —
  // over Netlify's ~26s sync cap — so we never call it inline here (that 502'd);
  // the background fn does the slow fetch and we read the fast cache.
  let d = date;
  if (!d) {
    const t = new Date();
    d = `${String(t.getMonth() + 1).padStart(2, '0')}/${String(t.getDate()).padStart(2, '0')}/${t.getFullYear()}`;
  }
  const WARM_S = 90;     // refresh in the background once the cache is older than this

  let cache = null;
  try { cache = await neonQuery('shipping_report_cache_get', { date: d }); } catch { /* noop */ }
  if (cache && cache.payload) {
    if ((cache.ageSec ?? 99999) > WARM_S) triggerShippingRefresh(d); // keep it warm
    return buildShippingResult(cache.payload, d);
  }

  // Cold cache: kick the background refresh and poll until it lands (~25s).
  triggerShippingRefresh(d);
  for (let i = 0; i < 12; i++) {
    await new Promise(r => setTimeout(r, 4000));
    try {
      const c = await neonQuery('shipping_report_cache_get', { date: d });
      if (c && c.payload) return buildShippingResult(c.payload, d);
    } catch { /* keep polling */ }
  }
  throw new Error('Production is taking longer than usual to build this report. Give it a moment and reload.');
}

// Profit-per-box report — one-paragraph AI read of a shipping day (Reports →
// Profit Per Box). Sends aggregates + a compact per-box list to the summary fn.
export async function fetchProfitPerBoxSummary(payload) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const res = await fetch(`${base}/profit-per-box-summary`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `AI summary ${res.status}`);
  return d;
}

// ── Packing costs (box + gel + dry ice) ──────────────────────────────────────
// Editable config (Fulfillment → Packing Costs) that turns packing quantities
// into a per-box cost. Box + gel unit costs are seeded from / refreshable from
// WOS (WarehouseOS); dry ice has no per-lb figure in WOS so it's a manual rate.
export async function fetchPackingCostConfig() {
  const res = await neonQuery('packing_cost_config_get', {});
  return (res && res.config) || res || {};
}
export async function savePackingCostConfig(config) {
  const res = await neonQuery('packing_cost_config_set', { config });
  return (res && res.config) || res || {};
}
export async function fetchWosInventory() {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const res = await fetch(`${base}/wos-proxy?endpoint=${encodeURIComponent('/api/InventoryApi/InventoryReport')}`);
  if (!res.ok) throw new Error(`WOS error: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.data || data.Data || []);
}
// Pull the current box + gel unit costs from WOS into a copy of the config
// (dry ice + per-case count are left alone — those aren't in WOS).
export async function refreshPackingCostsFromWos(config) {
  const inv = await fetchWosInventory();
  const byItem = {};
  inv.forEach(r => { if (r && r.item) byItem[String(r.item).toUpperCase()] = r; });
  const next = JSON.parse(JSON.stringify(config || {}));
  (next.boxes || []).forEach(b => {
    const w = byItem[String(b.wosItem || b.boxType || '').toUpperCase()];
    if (w && w.inventoryCost != null) b.cost = Number(w.inventoryCost);
  });
  if (next.gel) {
    const w = byItem[String(next.gel.wosItem || 'CP200').toUpperCase()];
    if (w && w.inventoryCost != null) next.gel.caseCost = Number(w.inventoryCost);
  }
  return next;
}
// Box types come off the order as e.g. "CP30" or "CP30 (18X17X15)" or "NO BOX".
function normalizeBoxType(bt) {
  return String(bt || '').toUpperCase().replace(/\s*\(.*$/, '').trim();
}
// packing = { boxType, gelPacks (count), dryIce (lbs) }
export function computePackingCost(config, packing) {
  const cfg = config || {};
  const boxes = Array.isArray(cfg.boxes) ? cfg.boxes : [];
  const bt = normalizeBoxType(packing.boxType);
  const noBox = !bt || bt === 'NO BOX' || bt === 'NOBOX';
  const match = boxes.find(b => String(b.boxType || '').toUpperCase() === bt);
  const box = noBox ? 0 : Number(match?.cost ?? 0);
  const caseCost = Number(cfg.gel?.caseCost ?? 0);
  const perCase = Number(cfg.gel?.perCase ?? 0) || 1;
  const gelUnit = caseCost / perCase;
  const gelCount = Number(packing.gelPacks ?? 0) || 0;
  const gel = gelCount * gelUnit;
  const dryRate = Number(cfg.dryIce?.perLb ?? 0);
  const dryLbs = Number(packing.dryIce ?? 0) || 0;
  const dryice = dryLbs * dryRate;
  return {
    boxType: bt, box, missingBox: !noBox && !match, noBox,
    gelUnit, gelCount, gel, dryRate, dryLbs, dryice,
    total: box + gel + dryice,
  };
}

// Order Profit modal (Shipping Today → click a profit badge). Reads the REAL
// packing + cost breakdown for one order straight from the production API
// (/Orders/OrderInformation → orderInfo/customerInfo/productInfo/products),
// via the auth-injecting proxy. READ-ONLY for now — no write-back until we wire
// the live "Save Costs"/"Mark as Done" endpoints. Field names verified against
// the order sync (sync-incremental.js upsertOrder/upsertItems).
export async function fetchOrderProfit(internalOrderId) {
  const raw = await apiGet('/Orders/OrderInformation', { orderId: internalOrderId, _host: 'prod' });
  const oi = raw.orderInfo ?? {};
  const ci = raw.customerInfo ?? {};
  const pi = raw.productInfo ?? {};
  const products = raw.products ?? [];
  const n = (v) => Number(v ?? 0) || 0;

  const productLines = products.map(p => {
    const qty = n(p.numItems ?? p.numPackingItems ?? 1) || 1;
    const unit = n(p.unitPrice);
    const options = (p.features ?? [])
      .map(f => `${f.feature ?? f.Feature ?? ''} ${f.value ?? f.Value ?? ''}`.trim())
      .filter(Boolean)
      .join(' | ');
    return { qty, name: p.name ?? p.externalName ?? '', options, price: unit, subTotal: unit * qty, cost: n(p.ourCost) };
  });

  const shipLabel = oi.shipMethodType || pi.shipMethodType || 'Shipping';
  const adjustment = n(pi.adjustment);
  const coupon = n(pi.couponDiscount ?? oi.couponDiscount);
  const gift = n(pi.giftAmountUsed);
  const sat = n(pi.satSurcharge);
  const hiak = n(pi.hiAkSurcharge ?? pi.hIAKSurcharge);

  // Non-packing cost lines (shipping + adjustments/coupons/surcharges). Packing is
  // computed separately below and is editable per-order in the modal.
  const otherCostLines = [
    { label: shipLabel, charge: n(pi.shipCost ?? oi.invoiceShipCost), cost: n(pi.shipOurCost ?? oi.shipOurCost) },
  ];
  if (adjustment || n(pi.adjustOurCost)) otherCostLines.push({ label: pi.adjustmentDescription ? `Adjustment (${pi.adjustmentDescription})` : 'Adjustment', charge: adjustment, cost: n(pi.adjustOurCost) });
  if (coupon || pi.coupon) otherCostLines.push({ label: pi.coupon ? `Coupon: ${pi.coupon}` : 'Coupon', charge: -coupon, cost: n(pi.couponOurCost) });
  if (sat || n(pi.satOurCost)) otherCostLines.push({ label: 'Saturday Surcharge', charge: sat, cost: n(pi.satOurCost) });
  if (hiak || n(pi.hiakOurCost)) otherCostLines.push({ label: 'AK/HI Surcharge', charge: hiak, cost: n(pi.hiakOurCost) });
  if (gift || n(pi.giftOurCost)) otherCostLines.push({ label: 'Gift Certificate', charge: -gift, cost: n(pi.giftOurCost) });

  // Packing: production quantities + any CPx per-order override (CPx-only, never
  // written back to production). The modal edits these and recomputes live.
  const prodPackQty = { boxType: pi.boxType ?? '', gelPacks: n(pi.gelPacks), dryIce: n(pi.dryice ?? pi.dryIce) };
  let packConfig = null, override = null;
  try { packConfig = await fetchPackingCostConfig(); } catch { /* noop */ }
  try { const o = await neonQuery('packing_override_get', { orderId: String(internalOrderId) }); override = (o && o.override) || null; } catch { /* noop */ }
  const effPackQty = override ? {
    boxType: override.box_type != null ? override.box_type : prodPackQty.boxType,
    gelPacks: override.gel_packs != null ? Number(override.gel_packs) : prodPackQty.gelPacks,
    dryIce: override.dry_ice != null ? Number(override.dry_ice) : prodPackQty.dryIce,
  } : prodPackQty;
  const savedPackingCost = n(pi.packOurCost);

  const productCost = productLines.reduce((s, l) => s + l.cost, 0);
  const otherCost = otherCostLines.reduce((s, l) => s + l.cost, 0);
  const merchandiseSubtotal = productLines.reduce((s, l) => s + l.subTotal, 0);
  const netSubtotal = merchandiseSubtotal - coupon - gift + adjustment;
  // Revenue collected = what the customer actually paid (merchandise + shipping − discounts).
  const orderAmount = n(pi.orderAmount ?? oi.orderAmount) || netSubtotal;

  return {
    orderId: String(oi.orderId ?? internalOrderId),
    bcOrderId: String(oi.bCOrderId ?? oi.bcOrderId ?? ''),
    deliveryDate: oi.deliveryDate ?? '',
    officeNotes: oi.officeNotes ?? oi.comment ?? '',
    packingComment: oi.packingComment ?? '',
    billTo: { name: [ci.firstName, ci.lastName].filter(Boolean).join(' '), line1: ci.address ?? '', city: ci.city ?? '', state: ci.state ?? '', zip: ci.postCode ?? '', phone: ci.phone ?? '', email: ci.customerEmail ?? ci.email ?? '' },
    shipTo: { name: [ci.shipName, ci.shipLastName].filter(Boolean).join(' '), line1: ci.shipAddress ?? '', city: ci.shipTown ?? '', state: ci.shipState ?? '', zip: ci.shipZip ?? '', phone: ci.shipPhone ?? '' },
    packingInfo: { steamedDate: pi.crabSteamedDate ?? '', packer: pi.packerName ?? '', crabCount: pi.crabCount ?? null },
    productLines, otherCostLines,
    packConfig, prodPackQty, override, effPackQty, savedPackingCost,
    productCost, otherCost, merchandiseSubtotal, netSubtotal, orderAmount,
  };
}

// The order's costs exactly as the live admin's Order Profit window reads them
// (products with their cost, productInfo with the *OurCost fields, merchant fees,
// server totals). Kept raw on purpose: saveOrderCosts posts the same objects back.
export async function fetchOrderCosts(internalOrderId) {
  return apiGet('/Orders/OrderProfitInformation', { OrderId: internalOrderId });
}

// Save Costs, the same call as the live admin's button. Writes OUR costs on the
// order (and the stored profit) in production. api-proxy allows this one write by
// exact path; see WRITE_ALLOWLIST there before adding another.
export async function saveOrderCosts(internalOrderId, products, productInfo) {
  const res = await fetch('/api/Orders/UpdateCostsInfo', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ orderId: Number(internalOrderId), packingNotes: '', products, productInfo }),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const j = await res.json(); if (j && (j.error || j.message)) msg = j.error || j.message; } catch (_) { /* keep status */ }
    throw new Error(msg);
  }
  return res.json().catch(() => ({}));
}

// Per-order packing override (CPx-side). Save/clear the box/gel/dry-ice used for
// an order's profit-per-box math without touching production.
export async function savePackingOverride(orderId, packing, updatedBy) {
  const res = await neonQuery('packing_override_set', {
    orderId: String(orderId),
    boxType: packing.boxType ?? null,
    gelPacks: packing.gelPacks ?? null,
    dryIce: packing.dryIce ?? null,
    updatedBy: updatedBy ?? null,
  });
  return (res && res.override) || null;
}
export async function deletePackingOverride(orderId) {
  return neonQuery('packing_override_delete', { orderId: String(orderId) });
}

// ─── Order Details ────────────────────────────────────────────────────────────

// Fetch CURRENT product thumbnails from the BC catalog API.
// Accepts product objects with an optional bcProductId (from the Neon
// order_items query) and name. Returns { [bcProductId]: url, ['name:'+name]: url }.
//
// It must be the LIVE catalog, not anything stored: products.cimageurl is a
// snapshot and every cdn11 URL in it 404s today, which is exactly why the order
// line items were showing the crab placeholder.
//
// Keyed on bcProductId, NEVER catalogId -- catalogId is the LEGACY catalog id
// (482, 269, 1390) and BC has only ~130 products, so feeding it to id:in= either
// misses or, worse, returns an unrelated product's photo.
const _bcImageCache = new Map(); // bcProductId|name-key -> url|null, per session
export async function fetchBcProductImages(products) {
  if (!products || products.length === 0) return {};
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions/bc-proxy'
    : '/.netlify/functions/bc-proxy';
  const _tok = getAccessToken();
  const _hdrs = { 'Content-Type': 'application/json' };
  if (_tok) _hdrs['Authorization'] = `Bearer ${_tok}`;
  const bcFetch = (path) => fetch(base, {
    method: 'POST',
    headers: _hdrs,
    body: JSON.stringify({ path, method: 'GET' }),
  }).then(r => r.json()).then(d => d?.data || []);
  const pickThumb = (images) => {
    const img = (images || []).find(im => im.is_thumbnail) || (images || [])[0];
    return img?.url_thumbnail || null;
  };

  const results = {};
  // Serve anything already resolved this session -- BC has ~130 products, so a
  // few order views cover most of the catalog and this saves a round trip each.
  for (const p of products) {
    if (p.bcProductId && _bcImageCache.has(p.bcProductId)) {
      const c = _bcImageCache.get(p.bcProductId);
      if (c) results[p.bcProductId] = c;
    }
  }
  try {
    // Primary route: the BC product id recovered from the legacy image URL.
    const withId = products.filter(p => p.bcProductId && !_bcImageCache.has(p.bcProductId));
    if (withId.length > 0) {
      const ids = [...new Set(withId.map(p => String(p.bcProductId)))];
      for (let i = 0; i < ids.length; i += 10) {
        const batch = ids.slice(i, i + 10);
        const data = await bcFetch(`/v3/catalog/products?id:in=${batch.join(',')}&include_fields=id&include=images&limit=50`);
        const seen = new Set();
        for (const p of data) {
          const url = pickThumb(p.images);
          seen.add(String(p.id));
          _bcImageCache.set(String(p.id), url || null);
          if (url) results[String(p.id)] = url;
        }
        // Cache the misses too, so a product BC no longer carries is not re-asked
        // on every order view.
        for (const id of batch) if (!seen.has(id)) _bcImageCache.set(id, null);
      }
    }
    // Fallback for the ~0.9% with no recoverable BC id: search by name.
    const withName = products.filter(p => !p.bcProductId && p.name);
    const uniqueNames = [...new Set(withName.map(p => p.name))];
    // Strip noise from order item names to get a searchable base name
    const toSearchName = (name) => {
      let s = name;
      // Remove leading quantity/size prefixes: "2 Dozen - ", "Half Dozen ", "2-pack, 8-10 oz.", "1 Pound "
      s = s.replace(/^(?:\d+-pack[,.]?\s*)?(?:\d+[-–]\d+\s*oz\.?\s*)?/i, '');
      s = s.replace(/^(?:\d+\s+)?(?:dozen|half\s+dozen|bushel|pound|pounds|lb|lbs)\s*[-–—,.]?\s*/i, '');
      // Remove trailing suffixes like " - IQF arrives frozen", " (Wild-Caught)"
      s = s.replace(/\s*[-–—]\s*(?:IQF|arrives|ships|frozen|fresh).*$/i, '');
      s = s.replace(/\s*\(.*?\)\s*$/, '');
      // Remove "Mega-Monster " and similar marketing prefixes
      s = s.replace(/^(?:mega[-\s]?monster|jumbo|super|premium|extra[-\s]?large)\s*/i, '');
      return s.trim();
    };
    for (const name of uniqueNames) {
      const searchName = toSearchName(name);
      if (!searchName) continue;
      const data = await bcFetch(`/v3/catalog/products?name:like=${encodeURIComponent(searchName)}&include_fields=id,name&include=images&limit=5`);
      // Pick the best match — shortest name (most specific parent product)
      if (data.length > 0) {
        const sorted = data.sort((a, b) => a.name.length - b.name.length);
        const url = pickThumb(sorted[0].images);
        if (url) results[`name:${name}`] = url;
      }
    }
  } catch (_) {}
  return results;
}

export async function fetchBcStoreCredit(email) {
  if (!email) return { credit: 0, bcCustomerId: null };
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  // Store credit (public endpoint) — returns only the credit amount now (PR-F
  // strips the _debug{ id, email } block it used to leak).
  let credit = 0;
  try {
    const res = await fetch(`${base}/bc-store-credit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    credit = data.storeCredit ?? 0;
  } catch (_) { /* leave credit at 0 */ }
  // BC customer id via the admin bc-proxy lookup — through bcProxy() so it carries
  // the admin session token (survives BC_PROXY_AUTH_ENFORCE) and no longer depends
  // on the removed _debug block.
  let bcCustomerId = null;
  try {
    const res = await bcProxy({ path: `/v3/customers?email:in=${encodeURIComponent(email)}`, method: 'GET' });
    const data = await res.json();
    bcCustomerId = data?.data?.[0]?.id ?? null;
  } catch (_) { /* leave bcCustomerId null */ }
  return { credit, bcCustomerId };
}

export async function fetchUpsTracking(trackingNumber) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/ups-tracking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ trackingNumber }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data;
}

// Pushed delivery photo captured from UPS "Track Alert with Photo" (the pull
// Track API never returns the image bytes — UPS only pushes them to our
// webhook on delivery; ups-track-alert stores them in Neon). Returns
// { found, image (data URL), received_at } or { found:false }.
export async function fetchUpsDeliveryPhoto(trackingNumber) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  try {
    const res = await fetch(`${base}/ups-track-alert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'photo', tracking: trackingNumber }),
    });
    return await res.json();
  } catch (e) {
    return { found: false };
  }
}

export async function fetchOrderDetails(orderId) {
  // Try Neon first (faster, no IP restriction)
  try {
    const orderRows = await neonQuery('order_by_id', { orderId });
    if (orderRows.length > 0) {
      // Use internal orderid for items query (orderId param may be bc_order_id)
      const internalId = String(orderRows[0].orderid);
      const itemRows = await neonQuery('order_items', { orderId: internalId });
      return { _fromNeon: true, order: orderRows[0], items: itemRows };
    }
  } catch (_) { /* fall through to prod API */ }

  // Fallback: production API (via the /api proxy so the service-account auth
  // header is injected server-side — see getOrderInformation).
  try {
    const resolved = await apiGet('/Orders/GetCPXOrderIdWithBCOrderId', { orderId });
    const internalId = resolved?.orderId || resolved?.OrderId;
    if (internalId && internalId > 0) {
      return getOrderInformation(internalId);
    }
  } catch (e) {
    // A flagged not-found from the resolved-internal-id lookup is a real
    // answer — re-throw it so the UI shows "not found", not a fall-through.
    if (e?.notFound) throw e;
  }
  return getOrderInformation(orderId);
}

export async function fetchOrderNotes(orderId, typeId) {
  try {
    const rows = await neonQuery('order_notes', { orderId, typeId });
    if (rows.length > 0) return rows;
  } catch (_) {}
  return get('/Orders/OrderOfficeNotesByOrderId', { orderId, typeId });
}

// An order's Auth.net transactions (charge + any refund/credit), the same data
// the live admin's Transactions panel shows. transactionId is "{authNetId}#card#type";
// a refund row has cardType "Refunded"/"Credited".
export async function searchGiftCertificates(filters) {
  return neonQuery('gift_search', filters);
}

export async function fetchOrderPayments(orderId) {
  return neonQuery('order_payments', { orderId });
}

// Live order Problems notes (the live admin's "Problems" panel). The Neon mirror
// is stale, so this hits the production API via the proxy. Returns [{date, type, description, employee, ...}].
export async function fetchOrderProblems(orderId) {
  return apiGet('/Orders/OrderProblemsByOrderId', { orderId });
}

// Split/reship linkage from the live order (Neon's columns aren't synced for recent
// orders). orginalSplitOrderId set = a split; originalOrderId set = a reship.
export async function fetchOrderSplitInfo(orderId) {
  try {
    const d = await apiGet('/Orders/OrderInformation', { orderId });
    const oi = (d && d.orderInfo) || {};
    return {
      splitOriginal: Number(oi.orginalSplitOrderId) > 0 ? oi.orginalSplitOrderId : null,
      reshipOriginal: Number(oi.originalOrderId) > 0 ? oi.originalOrderId : null,
      bcOriginal: Number(oi.bcOriginalOrderId) > 0 ? oi.bcOriginalOrderId : null,
    };
  } catch { return {}; }
}

// Box-capacity state (Common BE orderInfo). Neon does not mirror these columns, so the
// Neon-first Order Details path reads them live. boxCount null means the calculator never
// ran for this order - every order before the feature - which is NOT one box.
function pickBoxState(oi) {
  return {
    boxCount: oi.boxCount ?? null,
    needsBoxReview: oi.needsBoxReview === true,
    boxReviewedAt: oi.boxReviewedAt ?? null,
    boxReviewedBy: oi.boxReviewedBy ?? null,
  };
}

export async function fetchOrderBoxState(internalId) {
  const d = await apiGet('/Orders/OrderInformation', { orderId: internalId });
  return pickBoxState((d && d.orderInfo) || {});
}

// Settle an oversized order: agree with the calculator or set it back to 1. Either way
// the order stops blocking packing slips and invoices on Shipping Today. Same endpoint
// the old admin's Order Details uses (GET by design on the Common BE side).
export async function setBoxReview(internalId, boxCount, reviewedBy) {
  return apiGet('/BoxCapacity/SetReview', { orderId: internalId, boxCount, reviewedBy: reviewedBy || '' });
}

// Pending Activity — Service Refunds (credited same-day refunds, with resolved flag)
export async function fetchPendingServiceRefunds(includeResolved = false) {
  return neonQuery('pending_service_refunds', { include_resolved: !!includeResolved });
}
// Pending Activity — Unfiled Claims (whole-day lates with no claim filed + 45-day expiry)
export async function fetchPendingUnfiledClaims() {
  return neonQuery('pending_unfiled_claims', {});
}
// Dismiss an unfiled claim (filed manually / window passed) so it drops off the
// list. { tracking_number } for one, or { before_date } to clear a backlog.
export async function dismissUnfiledClaim(params) {
  return neonQuery('unfiled_claims_dismiss', params);
}

export async function fetchOrderHistory(customerId) {
  // Try Neon first
  try {
    const rows = await neonQuery('orders_by_customer', { customerId });
    if (rows.length > 0) {
      const amounts = rows.map(r => Number(r.order_amount ?? 0)).filter(n => n > 0);
      const avg = amounts.length ? amounts.reduce((a, b) => a + b, 0) / amounts.length : 0;
      const dates = rows.map(r => r.order_date).filter(Boolean).sort();
      return {
        summary: {
          totalNumberOfOrders: rows.length,
          averageOrderAmount: avg.toFixed(2),
          averageProfitPerOrder: '0.00',
          firstOrderDate: dates[0] ? new Date(dates[0]).toLocaleDateString('en-US') : '',
          lastOrderDate: dates[dates.length - 1] ? new Date(dates[dates.length - 1]).toLocaleDateString('en-US') : '',
        },
        orders: rows.map(r => ({
          id: r.bc_order_id || r.id,
          internalId: r.id,
          date: r.order_date,
          amount: Number(r.order_amount ?? 0),
          status: r.status || '',
          shipMethod: r.ship_method || '',
        })).sort((a, b) => new Date(b.date) - new Date(a.date)),
      };
    }
  } catch (_) {}
  return get('/Orders/OrderHistoryByCustomerId', { customerId });
}

export function transformOrderFromNeon(raw, fallback = null) {
  if (!raw?._fromNeon) return null;
  try {
    const o = raw.order;
    const items = raw.items ?? [];

    // Derive status from legacy flags
    const status = Number(o.canceled) === 1 ? 'Cancelled'
      : Number(o.hold) === 1 ? 'On Hold'
      : Number(o.o_completed) === 1 ? 'Completed'
      : Number(o.oprocessed) === 1 ? 'Shipped'
      : 'Processing';

    const fmtD = (v) => {
      if (!v) return '';
      const d = new Date(v);
      const date = `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
      const h = d.getHours() % 12 || 12;
      const min = String(d.getMinutes()).padStart(2, '0');
      const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
      return `${date} ${h}:${min}${ampm}`;
    };

    const lineItems = [];
    // Customer-charged packaging (pack_cost), matching the original transaction —
    // not pack_our_cost (our cost) or the legacy opackourcost column (now unfilled).
    const packCost = Number(o.pack_cost ?? 0);
    lineItems.push({ label: 'Packing Materials', price: `$${packCost.toFixed(2)}` });
    const shipCost = Number(o.oshipcost ?? o.ship_cost ?? 0);
    const shipLabel = o.oshipmethodtype || 'Shipping';
    lineItems.push({ label: shipLabel, price: `$${shipCost.toFixed(2)}` });
    const adj = Number(o.adjustment ?? o.oadjustment ?? 0);
    const adjDescr = o.adjustment_description || o.oadjustmentdescr || '';
    lineItems.push({ label: `Adjustment ${adjDescr ? `(${adjDescr})` : '()'}`, price: `$${adj.toFixed(2)}` });
    const sat = Number(o.osatsurcharge ?? o.sat_surcharge ?? 0);
    lineItems.push({ label: 'Saturday Delivery', price: `$${sat.toFixed(2)}` });
    const hiak = Number(o.o_hia_ksurcharge ?? 0);
    if (hiak) lineItems.push({ label: 'AK/HI Surcharge', price: `$${hiak.toFixed(2)}` });
    const coupon = Number(o.coupondiscount ?? 0);
    if (coupon) lineItems.push({ label: `Coupon${o.coupon ? ` (${o.coupon})` : ''}`, price: `-$${coupon.toFixed(2)}` });
    const gift = Number(o.giftamountused ?? 0);
    if (gift) lineItems.push({ label: 'Gift Certificate', price: `-$${gift.toFixed(2)}` });

    const billName = [o.bill_first, o.bill_last].filter(Boolean).join(' ') || [o.first_name ?? o.ofirstname, o.last_name ?? o.olastname].filter(Boolean).join(' ');
    const shipName = [o.ship_first, o.ship_last].filter(Boolean).join(' ') || [o.oshipname, o.oshiplastname].filter(Boolean).join(' ') || billName;

    return {
      id: String(o.bc_order_id || o.orderid),
      internalId: String(o.orderid),
      status,
      orderDate: fmtD(o.odate),
      shipDate: fmtD(o.shipdate),
      // Sync writes to the underscore columns; the bare-name columns are a
      // dead pre-sync era and are always NULL. Read underscore first, fall
      // back to legacy for any old rows that still carry it.
      selectedDelivery: fmtD(o.delivery_date ?? o.deliverydate),
      scaleDelivery: fmtD(o.scale_date ?? o.scaledate),
      estimatedDelivery: fmtD(o.estimated_delivery_date ?? o.estimateddeliverydate),
      actualDelivery: fmtD(o.actual_delivery_date ?? o.actualdeliverydatetime),
      fraudScore: Number(o.fraudscore ?? 0),
      trackingNumber: o.vatnumber ?? '',
      shippingInstructions: o.ocomment ?? '',
      products: items.map(p => {
        // Parse features — may be JSON array, stringified, or broken "[object Object]"
        let opts = {};
        try {
          const raw = p.features || p.feature_titles;
          if (raw && typeof raw === 'string' && raw.startsWith('[')) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) arr.forEach(f => { if (f?.feature || f?.name) opts[f.feature || f.name] = f.value || f.option || ''; });
          } else if (p.feature_titles && typeof p.feature_titles === 'string') {
            // feature_titles can be "Steaming Options:Live,Seasoning Options:Old Bay"
            p.feature_titles.split(',').forEach(pair => {
              const [k, v] = pair.split(':');
              if (k && v) opts[k.trim()] = v.trim();
            });
          }
        } catch (_) {}
        return {
          id: p.id ?? p.catalog_id,
          catalogId: p.catalog_id,
          bcProductId: p.bc_product_id ?? null,
          sku: p.sku ?? p.partnumber ?? null,
          // DELIBERATELY NOT p.image_url. products.cimageurl is a stale snapshot
          // whose every cdn11 URL 404s, and seeding it here was the bug: the
          // enrichment below only runs on products with NO image, so a dead URL
          // suppressed the BC lookup entirely and every row fell back to the
          // crab placeholder. The live image comes from BC via bcProductId.
          image: null,
          name: p.name ?? '',
          qty: Number(p.quantity ?? 1),
          price: `$${Number(p.unit_price ?? 0).toFixed(2)}`,
          total: `$${(Number(p.unit_price ?? 0) * Number(p.quantity ?? 1)).toFixed(2)}`,
          options: opts,
        };
      }),
      lineItems,
      total: `$${Number(o.orderamount ?? 0).toFixed(2)}`,
      transaction: null,
      giftCertificate: o.giftcertificate ? { code: o.giftcertificate, amount: `$${gift.toFixed(2)}` } : null,
      couponDiscount: coupon || null,
      balance: o.obalance ?? null,
      packingNotes: o.packing_notes ?? '',
      giftMessage: o.ogiftmessage ?? '',
      customer: {
        id: String(o.ocustomerid ?? ''),
        isVip: false,
        vipJoinedDate: null,
        username: o.email ?? o.oemail ?? '',
        password: '',
        emailStatus: 'subscribed',
        smsStatus: 'subscribed',
        billing: {
          name: billName,
          address: [o.bill_addr1 ?? o.oaddress, o.bill_addr2 ?? o.oaddress2].filter(Boolean).join(' '),
          city: o.bill_city ?? o.ocity ?? '',
          state: o.bill_state ?? o.ostate ?? '',
          zip: o.bill_zip ?? o.opostcode ?? '',
          phone: o.bill_phone ?? o.ophone ?? o.phone ?? '',
          email: o.email ?? o.oemail ?? '',
          homeValue: '',
        },
        shipping: {
          name: shipName,
          address: [o.ship_addr1 ?? o.oshipaddress, o.ship_addr2 ?? o.oshipaddress2].filter(Boolean).join(' '),
          city: o.ship_city ?? o.oshiptown ?? '',
          state: o.ship_state ?? o.oshipstate ?? '',
          zip: o.ship_zip ?? o.oshipzip ?? '',
          phone: o.ship_phone ?? o.oshipphone ?? '',
          email: o.email ?? o.oemail ?? '',
          residential: Boolean(o.is_residential),
          homeValue: '',
        },
        history: { avgOrderAmount: '$0.00', avgProfitPerOrder: '$0.00', firstOrderDate: '', lastOrderDate: '', totalOrders: 0 },
      },
      shipMethod: o.oshipmethodtype ?? '',
      reshipOrders: [],
      crabSteamedDate: o.crab_steamed_date ?? null,
      packerName: o.packername ?? null,
      boxType: o.boxtype ?? null,
      gelPacks: o.gelpacks ?? null,
      dryIce: o.dryice ?? null,
      crabCount: o.crabcount ?? null,
      // Additional Order Information
      orderReferredBy: o.page_referer ?? o.refering_ad ?? '',
      takenBy: o.takenby ?? '',
      declaredValue: Number(o.declared_value ?? 0),
      insuranceCost: Number(o.insurancecost ?? 0),
      orderProfit: Number(o.oprofit ?? 0),
      ipRating: Number(o.fraudscore ?? 0),
      ipRatingGrade: Number(o.fraudscore ?? 0) === 0 ? 'B' : Number(o.fraudscore ?? 0) < 50 ? 'C' : 'F',
      boxWeight: Number(o.rated_weight ?? 0),
      scaleShipCost: Number(o.oshiprefcost ?? o.oshipinvcost ?? 0),
      isFraud: o.is_fraud === '1' || o.is_fraud === true,
      crabEvent: o.crab_event === '1' || o.crab_event === true,
      claimOrder: o.claim_order === '1' || o.claim_order === true,
      physicalGiftCardSent: o.physical_gift_card_sent === '1' || o.physical_gift_card_sent === true,
      productAlert: Number(o.prod_alert ?? 0) > 0,
      claimNumber: o.claim_number ?? '',
      saturdayDelivery: o.saturday_delivery === '1' || o.saturday_delivery === true,
    };
  } catch(e) {
    console.error('transformOrderFromNeon error:', e);
    return fallback;
  }
}

export function transformOrderDetails(raw, fallback = null) {
  if (!raw) return fallback;
  try {
    // API returns OrderInfoMergeVM with nested objects
    const oi = raw.orderInfo ?? {};          // OrderInfoResultVM
    const ci = raw.customerInfo ?? {};       // CustomerInfoVM
    const pi = raw.productInfo ?? {};        // ProductInfoVM
    const products = raw.products ?? [];     // List<OrderInfoProductVM>

    // Derive status from flags
    let status = 'Processing';
    if (oi.cancelled) status = 'Cancelled';
    else if (oi.oCompleted) status = 'Completed';
    else if (oi.hold) status = 'On Hold';

    // Build line items from productInfo cost breakdown
    const lineItems = [];
    const shipCost = Number(pi.shipCost ?? oi.invoiceShipCost ?? 0);
    if (shipCost) lineItems.push({ label: 'Shipping', price: `$${shipCost.toFixed(2)}`, isLink: false });
    const satSurcharge = Number(pi.satSurcharge ?? 0);
    if (satSurcharge) lineItems.push({ label: 'Saturday Surcharge', price: `$${satSurcharge.toFixed(2)}`, isLink: false });
    const hiakSurcharge = Number(pi.hIAKSurcharge ?? pi.hiakSurcharge ?? 0);
    if (hiakSurcharge) lineItems.push({ label: 'AK/HI Surcharge', price: `$${hiakSurcharge.toFixed(2)}`, isLink: false });
    const adjustment = Number(pi.adjustment ?? 0);
    if (adjustment) lineItems.push({ label: pi.adjustmentDescription || 'Adjustment', price: `$${adjustment.toFixed(2)}`, isLink: false });
    const couponDiscount = Number(pi.couponDiscount ?? oi.couponDiscount ?? 0);
    if (couponDiscount) lineItems.push({ label: `Coupon${pi.coupon ? ` (${pi.coupon})` : ''}`, price: `-$${couponDiscount.toFixed(2)}`, isLink: false });
    const giftUsed = Number(pi.giftAmountUsed ?? 0);
    if (giftUsed) lineItems.push({ label: 'Gift Certificate', price: `-$${giftUsed.toFixed(2)}`, isLink: false });
    const bcDiscount = Number(pi.bCManualDiscount ?? pi.bcManualDiscount ?? 0);
    if (bcDiscount) lineItems.push({ label: 'BC Discount', price: `-$${bcDiscount.toFixed(2)}`, isLink: false });

    const billingName = [ci.firstName, ci.lastName].filter(Boolean).join(' ');
    const shippingName = [ci.shipName, ci.shipLastName].filter(Boolean).join(' ');

    return {
      id: String(oi.bCOrderId || oi.bcOrderId || oi.orderId || ''),
      internalId: String(oi.orderId ?? ''),
      status,
      orderDate: oi.orderDate ?? '',
      shipDate: oi.shipDate ?? '',
      selectedDelivery: oi.deliveryDate ?? '',
      scaleDelivery: oi.scaledate ?? '',
      estimatedDelivery: oi.estimatedDeliveryDate ?? '',
      actualDelivery: oi.actualDeliveryDate ?? '',
      fraudScore: Number(oi.fraudScore ?? 0),
      trackingNumber: oi.tackingNumber ?? oi.trackingNumber ?? '',  // typo in backend: "TackingNumber"
      shippingInstructions: oi.shippingComment ?? '',
      products: products.map(p => ({
        id: p.orderItemId ?? p.catalogId,
        catalogId: p.catalogId,
        sku: p.sku ?? p.partNumber ?? null,
        image: p.imageUrl ?? null,
        name: p.name ?? p.externalName ?? '',
        qty: Number(p.numItems ?? p.numPackingItems ?? 1),
        price: `$${Number(p.unitPrice ?? 0).toFixed(2)}`,
        total: `$${(Number(p.unitPrice ?? 0) * Number(p.numItems ?? 1)).toFixed(2)}`,
        options: (p.features ?? []).reduce((acc, f) => { acc[f.feature] = f.value; return acc; }, {}),
      })),
      lineItems,
      total: `$${Number(pi.orderAmount ?? oi.orderAmount ?? 0).toFixed(2)}`,
      transaction: null,
      giftCertificate: pi.giftCertificate ?? null,
      couponDiscount: couponDiscount || null,
      balance: oi.balance ?? null,
      packingNotes: oi.packingComment ?? '',
      giftMessage: oi.giftMessage ?? '',
      customer: {
        id: String(ci.customerId ?? ''),
        isVip: Number(ci.vipStatus ?? 0) > 0,
        vipJoinedDate: null, // fetched separately via fetchVipJoinedDate
        username: ci.customerEmail ?? ci.email ?? '',
        password: ci.customerPassword ?? '',
        zinreloPoints: 0,
        emailStatus: 'subscribed',
        smsStatus: 'subscribed',
        billing: {
          name: billingName || '',
          address: [ci.address, ci.address2].filter(Boolean).join(' '),
          city: ci.city ?? '',
          state: ci.state ?? '',
          zip: ci.postCode ?? '',
          phone: ci.phone ?? '',
          email: ci.email ?? ci.customerEmail ?? '',
          homeValue: ci.homeValue ?? '',
        },
        shipping: {
          name: shippingName || billingName || '',
          address: [ci.shipAddress, ci.shipAddress2].filter(Boolean).join(' '),
          city: ci.shipTown ?? '',
          state: ci.shipState ?? '',
          zip: ci.shipZip ?? '',
          phone: ci.shipPhone ?? '',
          email: ci.shipEmail ?? ci.customerEmail ?? '',
          residential: Boolean(ci.residential ?? false),
          homeValue: ci.shippingHomeValue ?? '',
        },
        history: {
          avgOrderAmount: '$0.00',
          avgProfitPerOrder: '$0.00',
          firstOrderDate: '',
          lastOrderDate: '',
          totalOrders: 0,
        },
      },
      systemNotes: Boolean(oi.officeNotes),
      shipMethod: oi.shipMethodType ?? ci.shipMethodType ?? pi.shipMethodType ?? '',
      reshipOrders: raw.bCReshipedOrders ?? raw.reshipedOrders ?? [],
      crabSteamedDate: pi.crabSteamedDate ?? null,
      packerName: pi.packerName ?? null,
      boxType: pi.boxType ?? null,
      gelPacks: pi.gelPacks ?? null,
      dryIce: pi.dryIce ?? null,
      crabCount: pi.crabCount ?? null,
      ...pickBoxState(oi),
      // Additional Order Information
      orderReferredBy: oi.orderReferredBy ?? '',
      takenBy: oi.takenBy ?? '',
      declaredValue: Number(oi.declared_Value ?? 0),
      insuranceCost: Number(oi.insurancecost ?? 0),
      orderProfit: Number(oi.oProfit ?? 0),
      ipRating: Number(oi.ipRating ?? 0),
      ipRatingGrade: Number(oi.ipRating ?? 0) === 0 ? 'B' : Number(oi.ipRating ?? 0) < 50 ? 'C' : 'F',
      boxWeight: Number(pi.totalWeight ?? 0),
      scaleShipCost: Number(pi.shipOurCost ?? oi.shipOurCost ?? 0),
      isFraud: Boolean(oi.isFraud),
      crabEvent: Boolean(oi.crabEvent),
      claimOrder: Boolean(oi.claimOrder),
      physicalGiftCardSent: false,
      productAlert: Number(oi.prodAlert ?? 0) > 0,
      claimNumber: oi.claimNumber ?? '',
      saturdayDelivery: false,
    };
  } catch (e) {
    console.error('Order transform error:', e, raw);
    return fallback;
  }
}

// ─── Fulfillment ──────────────────────────────────────────────────────────────

export async function fetchRemainingToday(date) {
  try {
    // The page sends MM/DD/YYYY; the query wants the same day as YYYY-MM-DD.
    const [mm, dd, yyyy] = String(date || '').split('/');
    const rows = await neonQuery('orders_remaining_today', { date: yyyy ? `${yyyy}-${mm}-${dd}` : null });
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/OrderReports/GetRemainingOrders', { date });
}

// A read that lives on api-dev for most controllers and only on api-bak for some.
async function apiGetEitherHost(path, params) {
  try { return await apiGet(path, params); } catch (_) { return apiGet(path, { ...params, _host: 'bak' }); }
}
const fullName = (a, b) => [a, b].filter(Boolean).join(' ').trim();

// Product Alerts: the same list the live admin shows (usp_Select_Report_Product_Alerts):
// every order carrying a real product alert, newest first, with the alert's name.
// Type 99 is the "no real alert" marker and is left out there, so it is left out here.
// The Neon copy is the fallback: same rule, but it only knows the alert's number.
export async function fetchProductAlerts(pageNumber = 1, pageSize = 50) {
  try {
    const d = await apiGetEitherHost('/Orders/ProductAlerts', { PageSize: pageSize, PageNumber: pageNumber });
    if (d && Array.isArray(d.productAlerts)) {
      return {
        live: true,
        totalRows: Number(d.totalRows) || d.productAlerts.length,
        data: d.productAlerts.map(r => ({
          orderId: r.orderId, bcOrderId: r.bcOrderId || null,
          billTo: fullName(r.firstName, r.lastName) || '—',
          shipDate: r.shipDate, alert: r.productAlert || '',
        })),
      };
    }
  } catch (_) { /* fall through to the mirror */ }
  const rows = await neonQuery('orders_product_alerts', {});
  const offset = (pageNumber - 1) * pageSize;
  return {
    live: false,
    totalRows: rows.length,
    data: rows.slice(offset, offset + pageSize).map(r => ({
      orderId: r.id, bcOrderId: r.bc_order_id || null,
      billTo: fullName(r.first_name, r.last_name) || '—',
      shipDate: r.ship_date, alert: r.prod_alert ? `Alert type ${r.prod_alert}` : '',
    })),
  };
}

export async function fetchGiftCertificates(pageNumber = 1, pageSize = 50) {
  try {
    const rows = await neonQuery('orders_gift_certificates', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/OrderReports/GetPhysicalGiftCertificates', { PageNumber: pageNumber, PageSize: pageSize });
}

// ─── Orders ───────────────────────────────────────────────────────────────────

export async function fetchPlacedToday(params) {
  // params: { StartDate, EndDate, PageNumber, PageSize } — dates in MM/DD/YYYY
  const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
  const limit = parseInt(params?.PageSize || 50, 10);
  const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
  const rows = await neonQuery('orders_by_date', {
    startDate: toIso(params?.StartDate),
    endDate: toIso(params?.EndDate),
    limit, offset,
  });
  return { data: rows.map(neonToOrderRow), totalRows: rows.length + offset };
}

export async function fetchOrdersOnHold() {
  const rows = await neonQuery('orders_on_hold_list', {});
  const data = rows.map((r) => ({
    ...neonToOrderRow(r),
    orderDate: r.order_date,
    deliveryDate: r.delivery_date,
    expirationDate: r.expiration_date,
    isGift: !!r.is_gift,
  }));
  return { data, totalRows: data.length };
}

export async function fetchPendingCharge(params) {
  try {
    const rows = await neonQuery('orders_pending_charge', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/ChargeOrders/ChargeOrdersToday', params);
}

export async function fetchOpenClaims(params) {
  try {
    const rows = await neonQuery('orders_open_claims', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/ChargeOrders/ClaimOrders', params);
}

// Fraud Alerts: the live admin's queue (usp_Select_Orders_Frauds) - orders the fraud
// check flagged that nobody has yet marked as fraud or put on hold, newest first.
// Neon does not carry the fraud-check result, so its copy can only fall back to
// "any order with a fraud score", newest first; the page says which one it is showing.
export async function fetchFraudAlerts(pageNumber = 1, pageSize = 50) {
  try {
    const d = await apiGetEitherHost('/Alerts/SearchFraudAlerts', { PageSize: pageSize, PageNumber: pageNumber });
    if (d && Array.isArray(d.fraudAlerts)) {
      const place = (a) => (a ? [a.city, a.state].filter(Boolean).join(', ') : '');
      return {
        live: true,
        totalRows: Number(d.totalRows) || d.fraudAlerts.length,
        data: d.fraudAlerts.map(r => {
          const b = r.billingInfo || {}; const sh = r.shippingInfo || {};
          return {
            orderId: r.orderId, bcOrderId: r.bcOrderId || null,
            billTo: fullName(b.firstName, b.lastName) || '—', billPlace: place(b),
            shipTo: fullName(sh.firstName, sh.lastName), shipPlace: place(sh),
            email: b.email || sh.email || '',
            fraudScore: Number(r.fraudScore ?? 0), orderTotal: Number(r.orderAmount ?? 0),
          };
        }),
      };
    }
  } catch (_) { /* fall through to the mirror */ }
  const offset = (pageNumber - 1) * pageSize;
  const rows = await neonQuery('orders_fraud', { limit: pageSize, offset });
  return { live: false, data: rows.map(neonToOrderRow), totalRows: rows.length + offset };
}

// Dashboard counts for the Fraud Alerts and Product Alerts rows: the totals the two
// alert pages show, so the number on the dashboard and the list behind it cannot
// disagree. Null means "no trustworthy count" and the dashboard keeps what it had
// (the fraud mirror cannot count the live queue; see fetchFraudAlerts).
export async function fetchAlertQueueCounts() {
  const [fraud, product] = await Promise.all([
    fetchFraudAlerts(1, 50).catch(() => null),
    fetchProductAlerts(1, 50).catch(() => null),
  ]);
  return {
    'Fraud Alerts': fraud && fraud.live ? Number(fraud.totalRows) || 0 : null,
    'Product Alerts': product ? Number(product.totalRows) || 0 : null,
  };
}

export async function fetchAkHiOrders(params) {
  try {
    const rows = await neonQuery('orders_ak_hi', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/Orders/AKHIOrders', params);
}

export async function fetchAddressValidations(params) {
  try {
    const rows = await neonQuery('orders_address_validations', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/Orders/ShippingAddressValidations', params);
}

// Marketing > Customer Photos (netlify/functions/customer-photos.js). Staff session required.
export async function customerPhotosCall(body) {
  const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/customer-photos`, { method: 'POST', headers, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export async function fetchAccessPointReport() {
  return neonQuery('access_point_report', {});
}

export async function fetchAccessPointPickups() {
  return neonQuery('access_point_pickups', {});
}

export async function fetchUnitScreenFlags(days = 14) {
  return neonQuery('unit_screen_flags', { days });
}

export async function resolveUnitScreenFlag(bcOrderId, resolved, note, by) {
  return neonQuery('unit_screen_resolve', { bcOrderId, resolved, note, by });
}

// Yield Sheet (Azure exit WS8): reads from the SQL01 copy, writes through the admin-write door.
export async function fetchYieldLookups() {
  return (await neonQuery('yield_sheet_lookups'))[0];
}

export async function fetchYieldDates(params) {
  const [purchaseOrders, count] = await Promise.all([neonQuery('yield_sheet_dates', params), neonQuery('yield_sheet_dates_count', params)]);
  return { purchaseOrders, totalRows: count[0] ? count[0].totalRows : 0 };
}

export async function fetchYieldSheets(params) {
  const rows = await neonQuery('yield_sheet_search', params);
  return rows.map((r) => {
    const out = { ...r };
    ['totalAmount', 'transportationCost', 'totalCrabs', 'totalYield', 'totalCrabCount', 'totalMaleFright', 'totalFemaleFright', 'totalMaleTally', 'totalMaleRetailPrice', 'totalFemaleTally', 'totalFemaleRetailPrice']
      .forEach((k) => { out[k] = r[k] == null ? 0 : Number(r[k]); });
    return out;
  });
}

export async function fetchYieldSheet(id) {
  const q = (name) => neonQuery(name, { id });
  const [po, items, transportation, sheet, sheetItems, summaries] = await Promise.all([q('yield_sheet_po'), q('yield_sheet_po_items'), q('yield_sheet_transportation'), q('yield_sheet_sheet'), q('yield_sheet_items'), q('yield_sheet_summaries')]);
  return assembleYieldSheet({ po, items, transportation, sheet, sheetItems, summaries });
}

export async function fetchYieldImages(id) {
  const [invoice, transport] = await Promise.all([neonQuery('yield_sheet_invoice_images', { id }), neonQuery('yield_sheet_transport_images', { id })]);
  return { invoice: invoiceImageList(invoice), transport: transportImageList(transport) };
}

export async function fetchYieldRetailPrices() {
  return (await neonQuery('yield_sheet_retail_prices')).map((r) => ({ gradedId: r.gradedId, price: Number(r.price) }));
}

export async function yieldWrite(action, body = {}) {
  const res = await fetch('/.netlify/functions/admin-write', { method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() }, body: JSON.stringify({ action, ...body }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not save (${res.status})`);
  return data;
}

export async function yieldFileUrls(names) {
  if (!names.length) return {};
  const res = await fetch('/.netlify/functions/yield-sheet-file', { method: 'POST', headers: { 'Content-Type': 'application/json', ...sessionHeaders() }, body: JSON.stringify({ names }) });
  if (!res.ok) throw new Error(`Could not open the files (${res.status})`);
  return (await res.json()).urls || {};
}
export async function fetchHardCrabCosts(startDate, endDate) {
  return neonQuery('hard_crab_costs', { startDate, endDate });
}

export async function fetchHardCrabCalendarAP(month) {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return neonQuery('hard_crab_calendar_ap', { startDate: `${month}-01`, endDate: `${month}-${last}` });
}

export async function fetchProfitAnalysis(startDate, endDate) {
  return neonQuery('profit_analysis', { startDate, endDate });
}

export async function fetchProfitAnalysisDetail(startDate, endDate, method) {
  return neonQuery('profit_analysis_detail', { startDate, endDate, method });
}

// Staff write door (Azure exit WS8, admin-write.js). A refusal comes back as a sentence.
export async function adminWrite(action, body = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/admin-write`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
    body: JSON.stringify({ ...body, action }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not save (${res.status})`);
  return data;
}

export async function adminWriteLive(feature) {
  return (await adminWrite('write_flag', { feature })).live === true;
}

export async function fetchShippingHolidays({ from, to, type }) {
  return neonQuery('shipping_holidays', { from, to, type });
}

export async function fetchUpsBlocklist({ search, includeDeleted }) {
  return neonQuery('ups_access_point_blocklist', { search, includeDeleted });
}

export async function fetchStaffCustomerPick(f) {
  return neonQuery('staff_customer_pick', f);
}

export async function fetchAddressExceptions({ customerName, email }) {
  return neonQuery('shipping_address_exceptions', { customerName, email });
}

export async function fetchAddressOverrides({ customerName, email }) {
  return neonQuery('shipping_address_overrides', { customerName, email });
}

export async function fetchSpecialCharges(f) {
  return neonQuery('special_charges', f);
}

export async function fetchShippingMethodGroups() {
  return neonQuery('shipping_method_groups');
}

export async function fetchUnmappedIgnored({ search, includeDeleted }) {
  return neonQuery('bc_unmapped_ignored', { search, includeDeleted });
}

export async function fetchBcProductPick() {
  return neonQuery('bc_product_pick');
}

export async function fetchProductAlertConfig() {
  return neonQuery('product_alert_config');
}

export async function fetchProductAlertTypes() {
  return neonQuery('product_alert_types');
}

export async function fetchProductNamePick() {
  return neonQuery('product_name_pick');
}

export async function fetchCrabSuppliers(f) {
  return neonQuery('crab_suppliers_search', f);
}

export async function fetchEmailConfig() {
  return neonQuery('email_config_all');
}

export async function fetchVipConfig(fieldName) {
  return neonQuery('vip_config_value', { fieldName });
}

export async function fetchFraudConfig() {
  return neonQuery('fraud_config');
}

export async function fetchShippingTypes() {
  return neonQuery('shipping_type_names');
}

export async function fetchFraudAddresses(f) {
  return neonQuery('fraud_addresses_search', f);
}

export async function fetchOrderShipAddress(orderId) {
  return neonQuery('fraud_order_ship_address', { orderId });
}

export async function fetchInventoryLimits(f) {
  return neonQuery('inventory_limit_search', f);
}

export async function fetchInventoryOverrides(inventoryId) {
  return neonQuery('inventory_limit_overrides', { inventoryId });
}

export async function fetchInventoryLimitProducts() {
  return neonQuery('inventory_limit_products');
}

export async function fetchInventoryLimitAlerts(startDate, endDate) {
  return neonQuery('inventory_limit_alerts', { startDate, endDate });
}

export async function fetchFacebookLeads() {
  return neonQuery('facebook_leads', {});
}

export async function fetchFacebookAdRoi() {
  return neonQuery('facebook_ad_roi', {});
}

export async function fetchSalesReportAP(month, year) {
  const [rows, totals] = await Promise.all([
    neonQuery('sales_report_ap', { month, year }),
    neonQuery('sales_report_ap_totals', { month, year }),
  ]);
  return { rows, totals: totals[0] || {} };
}

export async function fetchLargeGroundOrdersAP({ from, to, cleared }) {
  return neonQuery('orders_large_ground_ap', { from, to, cleared });
}

export async function fetchLargeGroundOrders(params) {
  try {
    const rows = await neonQuery('orders_large_ground', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/Orders/LargeGround', params);
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export async function fetchClaimOrders(params) {
  try {
    const rows = await neonQuery('orders_claim_orders', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/ChargeOrders/ChargeClaimOrdersToday', params);
}

export async function fetchShippingInsurance(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const limit = parseInt(params?.PageSize || 50, 10);
    const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
    const startDate = toIso(params?.StartDate);
    const endDate = toIso(params?.EndDate);
    const [rows, countRows, headerRows] = await Promise.all([
      neonQuery('shipping_insurance', { startDate, endDate, limit, offset }),
      neonQuery('shipping_insurance_count', { startDate, endDate }),
    neonQuery('shipping_insurance_header', { startDate, endDate }),
    ]);
    const totalRows = Number(countRows[0]?.total ?? rows.length);
    return { header: headerRows[0] ?? {}, records: { data: rows, totalRows }, data: rows, totalRows };
  } catch (_) {}
  return get('/OrderReports/GetShippingInsuranceReport', params);
}

export async function fetchProfitReport(startDate, endDate) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const rows = await neonQuery('profit_report', { startDate: toIso(startDate), endDate: toIso(endDate) });
    return { data: rows, totalRows: rows.length };
  } catch (_) {}
  return get('/OrderReports/GetProfitReport', { StartDate: startDate, EndDate: endDate });
}

export async function fetchProfitReportAP(startDate, endDate) {
  const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
  const rows = await neonQuery('profit_report_ap', { startDate: toIso(startDate), endDate: toIso(endDate) });
  return { data: rows, totalRows: rows.length };
}

export async function fetchHardCrabCalendar(fromDate, toDate) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const rows = await neonQuery('hard_crab_calendar', { startDate: toIso(fromDate), endDate: toIso(toDate) });
    return rows;
  } catch (_) {}
  return get('/OrderReports/HardCrabReport', { fromDate, toDate });
}

export async function fetchOrderMethod(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const startDate = toIso(params?.StartDate);
    const endDate = toIso(params?.EndDate);
    const rows = await neonQuery(params?.view === 'ap' ? 'order_method_ap' : 'order_method', { startDate, endDate });
    return { data: rows, totalRows: rows.length };
  } catch (_) {}
  return get('/OrderMethod/SearchOrderMethods', params);
}

export async function fetchNewVsReturning(month, year) {
  try {
    const rows = await neonQuery('new_vs_returning', { month, year });
    return rows;
  } catch (_) {}
  return get('/Reports/NewVsReturningReport', { month, year });
}

export async function fetchNewVsReturningAP(month, year) {
  const rows = await neonQuery('new_vs_returning_ap', { month, year });
  return { data: rows, totalRows: rows.length };
}

export async function fetchReferringAd(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const limit = parseInt(params?.PageSize || 50, 10);
    const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
    const startDate = toIso(params?.StartDate);
    const endDate = toIso(params?.EndDate);
    const [rows, countRows] = await Promise.all([
      neonQuery('referring_ad', { startDate, endDate, limit, offset }),
      neonQuery('referring_ad_count', { startDate, endDate }),
    ]);
    return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
  } catch (_) {}
  return get('/Reports/SearchReferringAd', params);
}

export async function fetchReferringAdAP(params) {
  const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
  const limit = parseInt(params?.PageSize || 50, 10);
  const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
  const startDate = toIso(params?.StartDate);
  const endDate = toIso(params?.EndDate);
  const [rows, countRows] = await Promise.all([
    neonQuery('referring_ad_ap', { startDate, endDate, limit, offset }),
    neonQuery('referring_ad_ap_count', { startDate, endDate }),
  ]);
  return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
}

export async function fetchStateBreakdown(params) {
  try {
    const toIso = (d) => { if (!d) return null; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const disableDates = params?.DisableDates === true || params?.DisableDates === 'true';
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:8888/.netlify/functions'
      : '/.netlify/functions';
    const neonParams = { DisableDates: disableDates, Dimension: params?.Dimension === 'billing' ? 'billing' : 'ship' };
    if (!disableDates) {
      neonParams.startDate = toIso(params?.StartDate);
      neonParams.endDate = toIso(params?.EndDate);
    }
    const res = await fetch(`${base}/neon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: 'state_breakdown_dynamic', params: neonParams }),
    });
    if (!res.ok) throw await neonError(res);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return { stateBreakdowns: data.rows, totalOrders: data.totalOrders ?? 0 };
  } catch (_) {}
  return get('/Reports/SearchStateBreakdown', params);
}

export async function fetchUnfinishedOrders(params) {
  try {
    const rows = await neonQuery('orders_unfinished', {});
    return { data: rows.map(neonToOrderRow), totalRows: rows.length };
  } catch (_) {}
  return get('/Orders/UnfinishedOrders', params);
}

export async function fetchUnfinishedOrdersAP(params) {
  const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; if (/^[0-9]{4}-/.test(d)) return d.slice(0, 10); const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
  const limit = parseInt(params?.PageSize || 50, 10);
  const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
  const startDate = toIso(params?.StartDate);
  const endDate = toIso(params?.EndDate);
  const [rows, countRows] = await Promise.all([
    neonQuery('unfinished_orders_ap', { startDate, endDate, limit, offset }),
    neonQuery('unfinished_orders_ap_count', { startDate, endDate }),
  ]);
  return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
}

export async function fetchOrderDataExport(params) {
  const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; if (/^[0-9]{4}-/.test(d)) return d.slice(0, 10); const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
  const limit = parseInt(params?.PageSize || 100, 10);
  const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
  const startDate = toIso(params?.StartDate);
  const endDate = toIso(params?.EndDate);
  const [rows, countRows] = await Promise.all([
    neonQuery('order_data_export', { startDate, endDate, limit, offset }),
    neonQuery('order_data_export_count', { startDate, endDate }),
  ]);
  return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
}

export async function fetchHomeValue({ customerId, address, city, state, zipCode, isBillingAddress = true }) {
  const token = getAccessToken();
  const base = window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const res = await fetch(`${base}/home-value`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ customerId, address, city, state, zipCode, isBillingAddress }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) throw new Error(data.error || `Home value lookup failed (${res.status})`);
  return data;
}

export async function fetchLateDelivery(params) {
  try {
    const toIso = (d) => { if (!d) return null; if (/^\d{4}-/.test(d)) return d.slice(0, 10); const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const startDate = toIso(params?.StartDate) || `${new Date().getFullYear()}-01-01`;
    const endDate = toIso(params?.EndDate) || new Date().toISOString().split('T')[0];
    const rows = await neonQuery('orders_refunds_reships', { startDate, endDate });
    return { data: rows, totalRows: rows.length };
  } catch (_) {}
  return get('/Orders/LateDelivery', params);
}

// ─── Profit Margin ────────────────────────────────────────────────────────────

export async function fetchProfitMarginReport(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const limit = parseInt(params?.PageSize || 50, 10);
    const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
    const startDate = toIso(params?.StartDate);
    const endDate = toIso(params?.EndDate);
    const [rows, countRows] = await Promise.all([
      neonQuery('profit_margin_report', { startDate, endDate, limit, offset }),
      neonQuery('profit_margin_report_count', { startDate, endDate }),
    ]);
    return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
  } catch (_) {}
  return get('/OrderReports/GetProfitMarginReport', params);
}

export async function fetchProfitMarginByProduct(startDate, endDate) {
  return neonQuery('profit_margin_by_product', { startDate, endDate });
}

export async function fetchProfitMarginByProductDetail(startDate, endDate, categoryId) {
  return neonQuery('profit_margin_by_product_detail', { startDate, endDate, categoryId });
}

export async function fetchProfitMarginDetail(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const limit = parseInt(params?.PageSize || 50, 10);
    const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
    const startDate = toIso(params?.StartDate);
    const endDate = toIso(params?.EndDate);
    const [rows, countRows] = await Promise.all([
      neonQuery('profit_margin_detail', { startDate, endDate, limit, offset }),
      neonQuery('profit_margin_detail_count', { startDate, endDate }),
    ]);
    return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
  } catch (_) {}
  return get('/OrderReports/GetProfitMarginDetailReport', params);
}

// ─── Customers ────────────────────────────────────────────────────────────────

export async function fetchCustomers(params) {
  try {
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:8888/.netlify/functions'
      : '/.netlify/functions';
    const res = await fetch(`${base}/neon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: 'customer_search_paginated', params }),
    });
    if (!res.ok) throw await neonError(res);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return { data: data.rows, totalRows: data.totalRows ?? data.rows.length };
  } catch (_) {}
  return get('/Customer/SearchCustomers', params);
}

export async function fetchVipMembers(params) {
  try {
    const [memberRows, countRows] = await Promise.all([
      neonQuery('vip_search_members', params),
      neonQuery('vip_search_count', params),
    ]);
    return {
      vipMembers: memberRows,
      totalRows: Number(countRows[0]?.total ?? memberRows.length),
    };
  } catch (_) {}
  return get('/VIPMembers/SearchVipClubMembers', params);
}

export async function fetchBestVipCustomers(topN = 25) {
  const rows = await neonQuery('vip_top_spenders', { limit: topN, active: true });
  return rows.map(r => {
    const joined = r.joined_date ? new Date(r.joined_date) : null;
    const yearsSinceJoining = joined ? Math.max(0.1, (Date.now() - joined.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 1;
    const totalOrders = Number(r.order_count ?? 0);
    const avgOrderAmount = Number(r.avg_order_value ?? 0);
    return {
      customerName: [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email,
      customerEmail: r.email,
      joinedDate: r.joined_date ?? null,
      yearsSinceJoining,
      totalOrders,
      avgOrderAmount,
      ordersPerYear: totalOrders / yearsSinceJoining,
    };
  }).sort((a, b) => b.ordersPerYear - a.ordersPerYear);
}

// Overlay helper: given a list of order numbers (BC order ids), returns a Set of
// those whose customer is an ACTIVE VIP that has opted OUT of auto-renewal.
// Used where the source feed carries only a plain VIP flag (e.g. the production
// profit report). Reuses ship-insert-lookup, which the live admin also calls.
export async function fetchVipNotRenewing(orderNumbers) {
  const nums = (orderNumbers || []).map(n => String(n).trim()).filter(Boolean);
  if (!nums.length) return new Set();
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/ship-insert-lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderNumbers: nums }),
  });
  if (!res.ok) throw new Error(`ship-insert-lookup ${res.status}`);
  const data = await res.json();
  return new Set((data.orders || []).filter(o => o.vip_not_renewing).map(o => String(o.order_number)));
}

export async function fetchVipRecord(email) {
  if (!email) return { joinedYear: null, vipColorStatus: 'never', vipNotRenewing: false };
  // Try Neon first
  try {
    const rows = await neonQuery('vip_by_email', { email });
    if (rows.length > 0) {
      const row = rows[0];
      const d = new Date(row.joined_date);
      const joinedYear = isNaN(d) ? null : String(d.getFullYear());
      const vipColorStatus = row.active ? 'active' : 'expired';
      // auto_renew=false on an ACTIVE membership = opted out of renewal.
      const vipNotRenewing = !!row.active && row.auto_renew === false;
      return { joinedYear, vipColorStatus, vipNotRenewing };
    }
    return { joinedYear: null, vipColorStatus: 'never', vipNotRenewing: false };
  } catch (_) {}
  // Fallback to production API
  try {
    const active = await get('/VIPMembers/SearchVipClubMembers', {
      Email: email, Filter_Email: true,
      Status: 'Active', Filter_Status: true,
      PageNumber: 1, PageSize: 1,
    });
    const activeMember = active?.vipMembers?.[0];
    if (activeMember) {
      const d = new Date(activeMember.joinedDate);
      return {
        joinedYear: isNaN(d) ? null : String(d.getFullYear()),
        vipColorStatus: 'active',
        // ⚠️ The API's `doNotRenewal` is INVERTED — it actually means "WILL renew"
        // (Selects.cs:137 flips the raw DoNotRenew column). So opted-OUT is
        // doNotRenewal === false. See sync-vip-scheduled.js for the full trace.
        vipNotRenewing: activeMember.doNotRenewal === false,
      };
    }
    const any = await get('/VIPMembers/SearchVipClubMembers', {
      Email: email, Filter_Email: true,
      PageNumber: 1, PageSize: 1,
    });
    const everVip = (any?.vipMembers?.length ?? 0) > 0;
    return { joinedYear: null, vipColorStatus: everVip ? 'expired' : 'never', vipNotRenewing: false };
  } catch (_) {
    return { joinedYear: null, vipColorStatus: 'never', vipNotRenewing: false };
  }
}

export async function fetchVipStats() {
  try {
    const rows = await neonQuery('vip_page_stats');
    const s = rows[0] ?? {};
    return {
      active: Number(s.active ?? 0),
      expired: Number(s.expired ?? 0),
      renewing: Number(s.renewing ?? 0),
      notRenewing: Number(s.not_renewing ?? 0),
      week: { newVips: Number(s.week_new ?? 0), reactivated: Number(s.week_reactivated ?? 0) },
      month: { newVips: Number(s.month_new ?? 0), reactivated: Number(s.month_reactivated ?? 0) },
    };
  } catch (_) {}
  const [active, expired, renewing, clubInfo] = await Promise.all([
    cachedGet('/VIPMembers/SearchVipClubMembers', { PageSize: 1, PageNumber: 1, Status: 'Active', Filter_Status: true }),
    cachedGet('/VIPMembers/SearchVipClubMembers', { PageSize: 1, PageNumber: 1, Status: 'Inactive', Filter_Status: true }),
    cachedGet('/VIPMembers/SearchVipClubMembers', { PageSize: 1, PageNumber: 1, Status: 'Active', Filter_Status: true, Renewal: 1 }),
    cachedGet('/Dashboard/GetVIPClubInfo').catch(() => null),
  ]);
  const activeCount = active?.totalRows ?? 0;
  const renewingCount = renewing?.totalRows ?? 0;
  const c = clubInfo ?? {};
  return {
    active: activeCount,
    expired: expired?.totalRows ?? 0,
    renewing: renewingCount,
    notRenewing: activeCount - renewingCount,
    week: { newVips: Number(c.weekTotalNewVip ?? 0), reactivated: Number(c.weekTotalReactivatedVIP ?? 0) },
    month: { newVips: Number(c.monthTotalNewVip ?? 0), reactivated: Number(c.monthTotalReactivatedVIP ?? 0) },
  };
}

export async function fetchVipGeoData() {
  try {
    const rows = await neonQuery('vip_by_state');
    if (rows.length > 0) return rows;
  } catch (_) {}
  return cachedGet('/Heatmap/SearchZipsForHeatmap', { Status: 1, ShipMethodType: 0 }, 30 * 60 * 1000);
}

// ─── Reconciliation ───────────────────────────────────────────────────────────

export async function fetchReconciliation(params) {
  try {
    const toIso = (d) => { if (!d) return null; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const neonParams = { ...params };
    if (params?.RequestedDateFrom) neonParams.RequestedDateFrom = toIso(params.RequestedDateFrom);
    if (params?.RequestedDateTo) neonParams.RequestedDateTo = toIso(params.RequestedDateTo);
    if (params?.ActualDateFrom) neonParams.ActualDateFrom = toIso(params.ActualDateFrom);
    if (params?.ActualDateTo) neonParams.ActualDateTo = toIso(params.ActualDateTo);
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:8888/.netlify/functions'
      : '/.netlify/functions';
    const res = await fetch(`${base}/neon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: 'reconciliation_search', params: neonParams }),
    });
    if (!res.ok) throw await neonError(res);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return { data: data.rows, totalRows: data.totalRows ?? data.rows.length };
  } catch (_) {}
  return get('/Orders/ShippingDeliveryReconcilationSearch', params);
}

export async function fetchDeliveryReconciliation(params) {
  return get('/Dashboard/OrdersDeliveryReconciliations', params);
}

export async function fetchDeliveryOntime(startDate, endDate) {
  const [summary, lateOrders, allOrders] = await Promise.all([
    neonQuery('delivery_ontime_summary', { startDate, endDate }),
    neonQuery('delivery_ontime_late_orders', { startDate, endDate }),
    neonQuery('delivery_ontime_all_orders', { startDate, endDate }),
  ]);
  return { summary: summary || [], lateOrders: lateOrders || [], allOrders: allOrders || [] };
}

// ─── Feedback ─────────────────────────────────────────────────────────────────

export async function fetchFeedbackSummary(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const limit = parseInt(params?.PageSize || 50, 10);
    const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
    const startDate = toIso(params?.FeedbackFromDate);
    const endDate = toIso(params?.FeedbackToDate);
    const [rows, countRows] = await Promise.all([
      neonQuery('feedback_summary', { startDate, endDate, limit, offset }),
      neonQuery('feedback_summary_count', { startDate, endDate }),
    ]);
    return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
  } catch (_) {}
  return get('/Feedback/SearchFeedbackSummary', params);
}

export async function fetchFeedbackDetails(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const limit = parseInt(params?.PageSize || 50, 10);
    const offset = ((parseInt(params?.PageNumber || 1, 10) - 1) * limit);
    const startDate = toIso(params?.FeedbackFromDate);
    const endDate = toIso(params?.FeedbackToDate);
    const [rows, countRows] = await Promise.all([
      neonQuery('feedback_details', { startDate, endDate, limit, offset }),
      neonQuery('feedback_summary_count', { startDate, endDate }),
    ]);
    return { data: rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
  } catch (_) {}
  return get('/Feedback/SearchFeedbackDetails', params);
}

// ─── Feedback CS Resolution (live data for the CS Resolution page) ────────────

export async function fetchFeedbackCsSummary({ startDate, endDate, limit = 500, offset = 0 }) {
  const [rows, countRows] = await Promise.all([
    neonQuery('feedback_cs_summary', { startDate, endDate, limit, offset }),
    neonQuery('feedback_cs_count', { startDate, endDate }),
  ]);
  return { data: rows || [], totalRows: Number(countRows?.[0]?.total ?? (rows?.length || 0)) };
}

// Resolve / re-open a feedback record. On resolve, the function generates a
// short AI summary from the order's internal notes server-side.
export async function resolveFeedback(payload) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/resolve-feedback`, {
    method: 'POST', headers, body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`resolve-feedback error: ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data;
}

export async function fetchFeedbackByCategory(params) {
  return get('/Feedback/SearchFeedbackSummaryByCategory', params);
}

export async function fetchFeedbackDetailsByCategory(params) {
  return get('/Feedback/SearchFeedbackDetailsByCategory', params);
}

export async function fetchFeedbackFollowUp(params) {
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const startDate = toIso(params?.StartDate);
    const endDate = toIso(params?.EndDate);
    const rows = await neonQuery('feedback_follow_up', { startDate, endDate });
    return { data: rows, totalRows: rows.length };
  } catch (_) {}
  return get('/Feedback/SearchEmailFollowUpOrders', params);
}

// ─── Sales Report ─────────────────────────────────────────────────────────────

export async function fetchSalesReport(date) {
  // date is MM/DD/YYYY — parse month/year for Neon queries
  const refDateParsed = new Date(date);
  const neonMonth = refDateParsed.getMonth() + 1;
  const neonYear = refDateParsed.getFullYear();

  // Helper to build the final response shape
  const buildResponse = (rows, vipRows, signupRows, reshipRows, year, month) => {
    const vipByDay = {};
    vipRows.forEach(v => { vipByDay[v.day] = v; });
    const signupsByDay = {};
    (signupRows || []).forEach(s => { signupsByDay[s.day] = s; });
    // Per-day reship count (subset of orderCount). base = orders - reships.
    const reshipsByDay = {};
    (reshipRows || []).forEach(r => { reshipsByDay[r.day] = Number(r.reshipCount ?? 0); });
    const monthName = new Date(year, month, 1).toLocaleString('en-US', { month: 'long' });
    const startDayOfWeek = new Date(year, month, 1).getDay();
    const lastDay = new Date(year, month + 1, 0).getDate();

    const days = rows.map(r => {
      const vip = vipByDay[r.day] ?? {};
      const signup = signupsByDay[r.day] ?? {};
      const vipOrders = Number(vip.orderCount ?? 0);
      const vipRev = Number(vip.totalSum ?? 0);
      const orderCount = Number(r.orderCount ?? 0);
      const totalSum = Number(r.totalSum ?? 0);
      const reships = reshipsByDay[r.day] ?? 0;
      return {
        date: r.day,
        orders: orderCount,
        // Non-reship base + reship count so the cell can render base⁺reships.
        ordersBase: Math.max(0, orderCount - reships),
        reships,
        revenue: `$${totalSum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        vipOrders,
        vipPct: orderCount ? Math.round(vipOrders / orderCount * 100) : 0,
        vipRevenue: `$${vipRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        vipRevPct: totalSum ? Math.round(vipRev / totalSum * 100) : 0,
        newVips: Number(vip.newVips ?? 0),
        // VIP membership purchases that day (matches the dashboard hero tile),
        // split new joiners vs lapsed members rejoining (winbacks).
        vipSignups: Number(signup.signups ?? 0),
        vipNew: Number(signup.newVips ?? 0),
        vipRejoined: Number(signup.rejoined ?? 0),
      };
    });

    const totalOrders = rows.reduce((s, r) => s + Number(r.orderCount ?? 0), 0);
    const totalReships = rows.reduce((s, r) => s + (reshipsByDay[r.day] ?? 0), 0);
    const totalRev = rows.reduce((s, r) => s + Number(r.totalSum ?? 0), 0);
    const totalVipOrders = vipRows.reduce((s, v) => s + Number(v.orderCount ?? 0), 0);
    const totalVipRev = vipRows.reduce((s, v) => s + Number(v.totalSum ?? 0), 0);

    return {
      month: `${monthName} ${year}`,
      range: `${String(month + 1).padStart(2, '0')}/01/${String(year).slice(2)} - ${String(month + 1).padStart(2, '0')}/${lastDay}/${String(year).slice(2)}`,
      summary: {
        totalOrders,
        totalReships,
        totalOrdersBase: Math.max(0, totalOrders - totalReships),
        revenue: `$${totalRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        vipOrders: totalVipOrders,
        vipPct: totalOrders ? Math.round(totalVipOrders / totalOrders * 100) : 0,
        vipRevenue: `$${totalVipRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        vipRevPct: totalRev ? Math.round(totalVipRev / totalRev * 100) : 0,
      },
      days,
      startDayOfWeek,
    };
  };

  // Try Neon first
  try {
    const [salesRows, vipRows, signupRows, reshipRows] = await Promise.all([
      neonQuery('sales_report_daily', { month: neonMonth, year: neonYear }),
      neonQuery('sales_report_vip_daily', { month: neonMonth, year: neonYear }).catch(() => []),
      neonQuery('sales_report_vip_signups_daily', { month: neonMonth, year: neonYear }).catch(() => []),
      neonQuery('sales_report_reships_daily', { month: neonMonth, year: neonYear }).catch(() => []),
    ]);
    if (salesRows.length > 0) {
      return buildResponse(salesRows, vipRows, signupRows, reshipRows, neonYear, neonMonth - 1);
    }
  } catch (_) {}

  // Fallback to production API
  const [salesData, vipData] = await Promise.all([
    get('/OrderReports/GetSalesReport', { date }),
    get('/OrderReports/GetSalesReportVIP', { date }).catch(() => null),
  ]);

  const rows = salesData?.data ?? [];
  const total = salesData?.total ?? {};
  const vipRows = vipData?.data ?? [];
  const vipByDay = {};
  vipRows.forEach(v => { vipByDay[v.day] = v; });

  const refDate = rows[0] ? new Date(rows[0].orderDate) : new Date(date);
  const year = refDate.getFullYear();
  const month = refDate.getMonth();
  const monthName = refDate.toLocaleString('en-US', { month: 'long' });
  const startDayOfWeek = new Date(year, month, 1).getDay();
  const lastDay = new Date(year, month + 1, 0).getDate();

  const days = rows.map(r => {
    const vip = vipByDay[r.day] ?? {};
    const vipOrders = Number(vip.orderCount ?? 0);
    const vipRev = Number(vip.totalSum ?? 0);
    const orderCount = Number(r.orderCount ?? 0);
    const totalSum = Number(r.totalSum ?? 0);
    return {
      date: r.day,
      orders: orderCount,
      revenue: `$${totalSum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      vipOrders,
      vipPct: orderCount ? Math.round(vipOrders / orderCount * 100) : 0,
      vipRevenue: `$${vipRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      vipRevPct: totalSum ? Math.round(vipRev / totalSum * 100) : 0,
      newVips: Number(vip.newVips ?? 0),
      // API fallback has no signup breakdown — show the legacy new-VIP count as the total
      vipSignups: Number(vip.newVips ?? 0),
      vipNew: Number(vip.newVips ?? 0),
      vipRejoined: 0,
    };
  });

  const totalOrders = Number(total.orderCount ?? rows.reduce((s, r) => s + Number(r.orderCount ?? 0), 0));
  const totalRev = Number(total.totalSum ?? rows.reduce((s, r) => s + Number(r.totalSum ?? 0), 0));
  const totalVipOrders = vipRows.reduce((s, v) => s + Number(v.orderCount ?? 0), 0);
  const totalVipRev = vipRows.reduce((s, v) => s + Number(v.totalSum ?? 0), 0);

  return {
    month: `${monthName} ${year}`,
    range: `${String(month + 1).padStart(2, '0')}/01/${String(year).slice(2)} - ${String(month + 1).padStart(2, '0')}/${lastDay}/${String(year).slice(2)}`,
    summary: {
      totalOrders,
      revenue: `$${totalRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      vipOrders: totalVipOrders,
      vipPct: totalOrders ? Math.round(totalVipOrders / totalOrders * 100) : 0,
      vipRevenue: `$${totalVipRev.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      vipRevPct: totalRev ? Math.round(totalVipRev / totalRev * 100) : 0,
    },
    days,
    startDayOfWeek,
  };
}

// ─── Daily Summary ────────────────────────────────────────────────────────────

// Property-local (Eastern) today as YYYY-MM-DD. odate is stored in ET, so the
// selectable day must be anchored to ET, not the admin's browser TZ / UTC.
export function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

// Returns { start, end, totals, categories: { existing_vip, previous_non_vip, new } }.
// Each category = { key, label, customerCount, orders, revenue, aov, signups, customers[] };
// totals = { customerCount, orders, revenue, aov, signups } across all buckets.
// One Neon call over the inclusive date range [start, end] (a single day passes
// start === end); buckets + aggregates are shaped here so the page stays dumb.
export async function fetchDailySummary(start, end = start, mode = 'placed') {
  // daily_summary excludes reships (original_order_id set — mostly $0 replacement
  // shipments) to keep order count + AOV revenue-accurate. We fetch the reship
  // count alongside so the UI can show it as a superscript (e.g. 1302⁺⁸⁷), which
  // reconciles with the live admin's raw "Orders Placed" count. Non-fatal.
  // mode: 'placed' = order date drives the range; 'delivered' = actual delivery
  // date drives it (for post-delivery follow-up).
  const [rows, reshipRows, winbackRows] = await Promise.all([
    neonQuery('daily_summary', { start, end, mode }),
    neonQuery('daily_summary_reships', { start, end, mode }).catch(() => []),
    // Anchored on the flip date, so a week's win backs land on that week
    // regardless of when each customer happened to order.
    neonQuery('winbacks_by_flip', { start, end }).catch(() => []),
  ]);

  // Customers credited as a CS win IN THIS RANGE. Deliberately built from
  // winbackRows (flip-date scoped) and not from each row's `won_back` flag: that
  // flag comes from daily_summary's outreach_flip CTE, which has no date filter
  // at all, so it is true for anyone ever flipped after an outreach send.
  const csWinIds = new Set((winbackRows || []).map((r) => String(r.ocustomerid)));

  const mk = (key, label) => ({ key, label, customers: [], orders: 0, revenue: 0, signups: 0, wonBack: 0 });
  const cats = {
    existing_vip: mk('existing_vip', 'Existing VIPs'),
    ex_vip: mk('ex_vip', 'Ex-VIP Customers'),
    repeat_never_vip: mk('repeat_never_vip', 'Repeat Never-VIP Customers'),
    new: mk('new', 'Brand-New Customers'),
  };

  for (const r of rows || []) {
    const c = cats[r.category];
    if (!c) continue;
    const todayRev = Number(r.today_rev) || 0;
    const todayOrders = Number(r.today_orders) || 0;
    const lifetimeOrders = Number(r.lifetime_orders) || 0;
    const lifetimeRev = Number(r.lifetime_rev) || 0;
    c.orders += todayOrders;
    c.revenue += todayRev;
    // A customer the CS team emailed who then ALSO bought the membership at
    // checkout lands in both halves. They are NOT mutually exclusive by
    // construction (winbacks_by_flip does not exclude membership line items), so
    // the overlap is removed here: count once, credit CS (Greg, 2026-08-14).
    const isCsWin = csWinIds.has(String(r.ocustomerid));
    const countsAsCheckout = !!r.signed_up_today && !isCsWin;
    if (countsAsCheckout) c.signups += 1;
    if (r.won_back) c.wonBack += 1;
    c.customers.push({
      id: r.ocustomerid,
      name: [r.firstname, r.lastname].filter(Boolean).join(' ').trim() || '(no name on file)',
      email: r.email || '',
      isVip: r.category === 'existing_vip',
      vipNotRenewing: !!r.vip_not_renewing,
      // Holds a membership RIGHT NOW, whatever they were during the range.
      // The four buckets describe the customer on the day they ordered, which
      // is what the history needs and the opposite of what the worklist needs:
      // Rachael hands out a May queue in August, so somebody who joined the
      // club in between is still, correctly, bucketed repeat_never_vip. Ashley
      // caught one on 2026-08-28 (Vance Meadows, a member since 28 June, on her
      // list of 26 invitations). This flag is what withdraws the offer.
      currentlyVip: !!r.currently_vip,
      currentlyVipSince: r.currently_vip_since || null,
      doNotContact: r.dnc_reason || null,
      hasChargeback: !!r.has_chargeback,
      noteRep: r.note_rep || null,
      noteRepAdminId: r.note_rep_admin_id != null ? String(r.note_rep_admin_id) : null,
      // False for a dual customer so the row shows the CS Win badge (which is
      // gated on !signedUpToday) and the badge agrees with where the count went.
      signedUpToday: countsAsCheckout,
      wonBack: !!r.won_back,
      // Bucket recorded at send time; null for sends predating the category
      // column. The live category can no longer be trusted for won-back rows —
      // the flip overwrites joined_date, which re-buckets them to
      // repeat_never_vip regardless of what they actually were.
      sentCategory: r.sent_category || null,
      flippedOn: r.flipped_on || null,
      outreachSentAt: r.outreach_sent_at || null,
      outreachRep: r.outreach_rep || null,
      outreachOffer: Number(r.outreach_offer_price) || 0,
      channel: r.channel || null,
      // In-range orders (newest first) for the linked order-number chips.
      // number = human-facing order # (bc_order_id when present, else internal id);
      // linkId is what Order Details resolves (order_by_id matches either).
      orders: (Array.isArray(r.order_list) ? r.order_list : [])
        .filter((o) => o && (o.orderid != null || o.bc_order_id != null))
        .map((o) => {
          const number = String(o.bc_order_id || o.orderid);
          // internalId is the legacy orderid, which is what the live admin's
          // office-notes API keys on - `number` is the BigCommerce one on most
          // orders and posting that would file the note against nothing.
          return { number, linkId: number, internalId: o.orderid == null ? null : String(o.orderid) };
        }),
      todayRev,
      todayOrders,
      lifetimeOrders,
      lifetimeRev,
      firstOrder: r.first_order_ts,
      lastOrder: r.last_order_ts,
      orders12mo: Number(r.orders_12mo) || 0,
      aov: lifetimeOrders ? lifetimeRev / lifetimeOrders : 0,
      // "Would've saved" as a VIP — George's exact model, per window.
      whsPeriod: Number(r.whs_period) || 0,
      whs12mo: Number(r.whs_12mo) || 0,
      whsAllTime: Number(r.whs_alltime) || 0,
    });
  }

  // Win backs credited to this range by FLIP date. Not derived from `cats` —
  // these customers need not have ordered in the range at all.
  const wonBackList = (winbackRows || []).map((r) => ({
    id: r.ocustomerid,
    name: [r.firstname, r.lastname].filter(Boolean).join(' ').trim() || '(no name on file)',
    email: r.email || '',
    flippedOn: r.flipped_on || null,
    outreachSentAt: r.outreach_sent_at || null,
    outreachRep: r.outreach_rep || null,
    outreachOffer: Number(r.outreach_offer_price) || 0,
    sentCategory: r.sent_category || null,
  }));

  const totals = {
    customerCount: 0, orders: 0, revenue: 0, signups: 0, wonBack: wonBackList.length,
    reships: Number(reshipRows?.[0]?.reships) || 0,
    reshipRev: Number(reshipRows?.[0]?.reship_rev) || 0,
  };
  for (const key of Object.keys(cats)) {
    const c = cats[key];
    c.customerCount = c.customers.length;
    c.aov = c.orders ? c.revenue / c.orders : 0;
    // Highest-value customers first within each bucket.
    c.customers.sort((a, b) => b.todayRev - a.todayRev);
    totals.customerCount += c.customerCount;
    totals.orders += c.orders;
    totals.revenue += c.revenue;
    totals.signups += c.signups;
  }
  totals.aov = totals.orders ? totals.revenue / totals.orders : 0;

  return { start, end, totals, categories: cats, wonBackList };
}

// Newsletter-signup date for an email, from Dotdigital (cached in Neon). Returns
// { signupDate: ISOstring|null, found } or null on error. Used by the Daily
// Summary to annotate Email-attributed brand-new customers.
export async function fetchDotdigitalSignup(email) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  try {
    const res = await fetch(`${base}/dotdigital-contact-date?email=${encodeURIComponent(email)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
}

// ─── VIP conversion outreach (Orders Summary email tool) ──────────────────────

// The logged-in rep's real name + email (browser session only carries staffName).
export async function fetchVipOutreachRep(adminId) {
  try {
    const rows = await neonQuery('vip_outreach_rep', { adminId });
    return Array.isArray(rows) && rows[0] ? rows[0] : null;
  } catch (_) { return null; }
}

// Which of the given customer ids have already been emailed a VIP invite.
// Returns { [customerId]: { sentAt, repName } }.
export async function fetchVipOutreachStatus(customerIds) {
  try {
    const rows = await neonQuery('vip_outreach_status', { customerIds });
    const map = {};
    (rows || []).forEach((r) => { map[String(r.customer_id)] = { sentAt: r.sent_at, repName: r.rep_name }; });
    return map;
  } catch (_) { return {}; }
}

// Log an outreach email as sent (customer, rep, would've-saved, offer, range).
export async function logVipOutreach(payload) {
  return neonQuery('vip_outreach_add', payload);
}

// Undo the most recent outreach log for a customer.
export async function undoVipOutreach(customerId, repName) {
  return neonQuery('vip_outreach_undo', { customerId, repName: repName || null });
}

// ─── VIP outreach delegation (Rachael hands out the day's queue) ──────────────

// Active staff carrying the Customer Service role — who work can be handed to.
export async function fetchOutreachAssignees() {
  try {
    const rows = await neonQuery('outreach_assignees', {});
    return Array.isArray(rows) ? rows : [];
  } catch (_) { return []; }
}

// Current assignment per customer for the rows on screen.
// Returns { [customerId]: { id, assigneeName, assigneeAdminId, dueAt, remindMinutes, done, sentAt, assignedBy, category } }.
export async function fetchOutreachAssignments(customerIds) {
  try {
    const rows = await neonQuery('outreach_assign_list', { customerIds });
    const map = {};
    (rows || []).forEach((r) => {
      map[String(r.customer_id)] = {
        id: r.id,
        assigneeName: r.assignee_name,
        assigneeAdminId: r.assignee_admin_id,
        dueAt: r.due_at,
        remindMinutes: r.remind_minutes,
        done: !!r.done,
        sentAt: r.sent_at,
        sentBy: r.sent_by,
        assignedBy: r.assigned_by_name,
        category: r.category,
        workDate: r.work_date,
      };
    });
    return map;
  } catch (_) { return {}; }
}

// Hand a batch of customers to one rep with a deadline.
export async function assignOutreach(payload) {
  return neonQuery('outreach_assign_set', payload);
}

// Take customers back off the list.
export async function clearOutreachAssignments(customerIds, workDate) {
  return neonQuery('outreach_assign_clear', { customerIds, workDate: workDate || null });
}

// Push a deadline out (default: tomorrow, same time).
export async function pushOutreachAssignments(customerIds, opts) {
  return neonQuery('outreach_assign_push', Object.assign({ customerIds }, opts || {}));
}

// Per-rep sent / replied counts for a date range.
export async function fetchOutreachScoreboard(start, end) {
  try {
    const rows = await neonQuery('outreach_rep_scoreboard', { start, end });
    return Array.isArray(rows) ? rows : [];
  } catch (_) { return []; }
}
// What the signed-in rep still has to send, on ANY date rather than the range the
// page happens to be showing. Returns { open, pastDue, firstDate, lastDate, rows }.
// Never throws: this drives a convenience badge, not the page.
export async function fetchMyOpenOutreach(adminId) {
  try {
    const r = await neonQuery('vip_outreach_my_open', { adminId: String(adminId) });
    return r && !Array.isArray(r) ? r : null;
  } catch (_) { return null; }
}

// ─── Shipping-tab box-insert follow-up ────────────────────────────────────────

// Which of the given order numbers already have their box insert marked done.
// Returns { [orderNumber]: { doneAt, repName } }.
export async function fetchShipInsertStatus(orderNumbers) {
  try {
    const rows = await neonQuery('ship_insert_status', { orderNumbers });
    const map = {};
    (rows || []).forEach((r) => { map[String(r.order_number)] = { doneAt: r.done_at, repName: r.rep_name }; });
    return map;
  } catch (_) { return {}; }
}

// Mark a customer's shipment order(s) as insert-done.
export async function markShipInsert(payload) {
  return neonQuery('ship_insert_add', payload);
}

// Undo insert-done for a customer's shipment order(s).
export async function undoShipInsert(orderNumbers) {
  return neonQuery('ship_insert_undo', { orderNumbers });
}

// ─── Yield Report ─────────────────────────────────────────────────────────────

export async function fetchYieldSheetList(dateReceived) {
  // dateReceived: 'MM/DD/YYYY' (also tolerates the DB's raw '2026-08-11 00:00:00' text)
  try {
    const toIso = (d) => {
      if (!d) return new Date().toISOString().split('T')[0];
      const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
      if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
      const [mm, dd, yyyy] = d.split('/');
      return `${yyyy}-${mm}-${dd}`;
    };
    const rows = await neonQuery('yield_sheets_by_date', { dateReceived: toIso(dateReceived) });
    if (rows.length > 0) return rows;
  } catch (_) {}
  const params = {};
  if (dateReceived) params.DateReceived = dateReceived;
  const data = await getSafe('/YieldSheet/SearchYieldSheet', params);
  return data?.purchaseOrders ?? data?.PurchaseOrders ?? [];
}

export async function fetchPurchaseOrderById(id) {
  // Build the whole yield-sheet detail from Neon (PO + items + yield sheet +
  // per-grade items + summary). The /YieldSheet/GetPurchaseOrderById API path is
  // kept only as a last resort — it now requires a staff session on api-proxy, so
  // it fails silently for this call; Neon is the real source.
  try {
    const [poRows, poItems, ysRows, ysItems, ysSummary] = await Promise.all([
      neonQuery('purchase_order_by_id', { id }),
      neonQuery('purchase_order_items', { id }).catch(() => []),
      neonQuery('yield_sheet_by_po', { id }).catch(() => []),
      neonQuery('yield_sheet_items_detail', { id }).catch(() => []),
      neonQuery('yield_sheet_summary_by_po', { id }).catch(() => []),
    ]);
    if (poRows.length > 0) {
      const po = poRows[0];
      return {
        purchaseOrder: po,
        purchaseOrderItems: poItems,
        purchaseOrderTransportation: { transportationCost: po.transportationCost ?? 0 },
        yieldSheet: ysRows[0] || {},
        yieldSheetItems: ysItems,
        yieldSheetSummary: ysSummary,
      };
    }
  } catch (_) {}
  return getSafe('/YieldSheet/GetPurchaseOrderById', { Id: id });
}

export async function fetchYieldSheetsByVendorHistory(startDate, endDate) {
  // startDate/endDate: 'MM/DD/YYYY' — fetches all POs in range for client-side vendor filtering
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const rows = await neonQuery('purchase_orders_by_date_range_flat', { startDate: toIso(startDate), endDate: toIso(endDate), limit: 1000 });
    if (rows.length > 0) return rows;
  } catch (_) {}
  const data = await getSafe('/YieldSheet/SearchYieldSheet', { StartDate: startDate, EndDate: endDate, PageSize: 1000 });
  return data?.purchaseOrders ?? data?.PurchaseOrders ?? [];
}

export async function fetchYieldSheetsByDateRange(startDate, endDate, pageNumber = 1, pageSize = 50) {
  // startDate/endDate: 'MM/DD/YYYY'
  try {
    const toIso = (d) => { if (!d) return new Date().toISOString().split('T')[0]; const [mm, dd, yyyy] = d.split('/'); return `${yyyy}-${mm}-${dd}`; };
    const isoStart = toIso(startDate);
    const isoEnd = toIso(endDate);
    const limit = pageSize;
    const offset = (pageNumber - 1) * pageSize;
    const [rows, countRows] = await Promise.all([
      neonQuery('yield_sheets_by_date_range', { startDate: isoStart, endDate: isoEnd, limit, offset }),
      neonQuery('yield_sheets_by_date_range_count', { startDate: isoStart, endDate: isoEnd }),
    ]);
    if (rows.length > 0) return { rows, totalRows: Number(countRows[0]?.total ?? rows.length) };
  } catch (_) {}
  const params = { StartDate: startDate, EndDate: endDate, PageNumber: pageNumber, PageSize: pageSize };
  const data = await getSafe('/YieldSheet/SearchPurchaseOrderGroupByDateReceived', params);
  return {
    rows: data?.purchaseOrders ?? data?.PurchaseOrders ?? [],
    totalRows: data?.totalRows ?? data?.TotalRows ?? 0,
  };
}

// ─── Order editing ─────────────────────────────────────────────────────────────

export async function saveOrderChanges(internalId, data) {
  return post('/Orders/UpdateOrdersInfo', {
    OrderId: parseInt(internalId, 10),
    TrackingNumber: data.trackingNumber ?? '',
    PackingNotes: data.packingNotes ?? '',
    GiftMessage: data.giftMessage ?? '',
    ShippingComments: data.shippingInstructions ?? '',
    DeliveryDate: new Date(0).toISOString(),
    SaturdaySurchargeCost: 0,
    HolidayAssuranceCost: 0,
    Done: false,
    Products: [],
  });
}

export async function setOrderHold(internalId, holdOn) {
  return post('/Orders/UpdateOrdersHoldStatus', {
    OrderId: parseInt(internalId, 10),
    HoldStatus: holdOn ? 1 : 0,
  });
}

export async function cancelOrder(internalId, employeeId, employeeName) {
  return post('/Orders/UpdateOrdersCanceled', {
    OrderId: parseInt(internalId, 10),
    EmployeeId: employeeId || 0,
    EmployeeName: employeeName || '',
  });
}

// ⚠️ GOES THROUGH A NETLIFY FUNCTION, NOT post(). This used to POST straight at
// BASE_URL from the browser, which could never work: it sent no access_token, and
// BASE_URL resolves to api-dev (the Netlify env var beats .env.production, because
// dotenv does not overwrite a variable that is already set) - so even authenticated
// it would have written the note to the DEV database, invisible to the people who
// read notes in the live admin. Measured 2026-09-01: of 12 VIP invitations sent that
// afternoon, zero carried the note. The token now lives server-side and the write
// lands where the admin reads it. See netlify/functions/order-note-add.js.
export async function addOrderNote(internalId, description, employee, typeId = 1) {
  return postFunction('order-note-add', {
    orderId: parseInt(internalId, 10),
    description,
    employee: employee || '',
    typeId,
  });
}

// ─── Order Desk (beta) ────────────────────────────────────────────────────────
// src/pages/OrderDesk.js. Its new data lives in netlify/functions/lib/order-desk.js;
// everything else on that page is the same calls Order Details makes.

// Customer relations entries, problems and logged texts for one order.
// -> { exists, entries, crTypes }. exists=false until the first entry is saved.
export async function fetchOrderDesk(orderId) {
  return neonQuery('order_desk_list', { orderId: String(orderId) });
}

// kind: 'cr' | 'problem' | 'contact'. Author and time are stamped by the server.
export async function addOrderDeskEntry({ orderId, kind, entryType, body }) {
  const rows = await neonQuery('order_desk_add', { orderId: String(orderId), kind, entryType, body });
  return rows[0];
}

export async function resolveOrderDeskProblem(id, resolution) {
  const rows = await neonQuery('order_desk_resolve', { id, resolution });
  return rows[0];
}

// Distinct ship-to addresses on this customer's orders (and same-person Linked Accounts).
export async function fetchPreviousShipAddresses(customerId) {
  return neonQuery('order_desk_ship_addresses', { customerId: String(customerId || '') });
}

// The rep who placed a crabplace.com order for the customer (order-taken-by.js), or null.
export async function fetchOrderTakenBy(bcOrderId) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  try {
    const res = await fetch(`${base}/order-taken-by?orderId=${encodeURIComponent(bcOrderId)}`);
    const d = await res.json();
    return d.takenBy || null;
  } catch (_) { return null; }
}

// UTM / click-id record for an order, or null.
export async function fetchOrderAttribution(bcOrderId) {
  try {
    const rows = await neonQuery('attribution_by_order', { bc_order_id: String(bcOrderId) });
    return rows[0] || null;
  } catch (_) { return null; }
}

// Text the customer through RingCentral - the same call as Order Details' "Text Customer".
// This SENDS a real text; callers confirm first.
export async function sendCustomerSms(to, text) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const phone = String(to || '').replace(/\D/g, '').replace(/^(\d{10})$/, '+1$1');
  const res = await fetch(`${base}/ringcentral-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'sms-send', params: { to: phone, text } }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'SMS send failed');
  return data;
}

// ─── Products ─────────────────────────────────────────────────────────────────

export async function fetchProductList(startDate, endDate) {
  // startDate/endDate: ISO date strings e.g. '2026-03-18T00:00:00'
  return post('/Orders/GetProductListPackerViewWithDateFilter', {
    StartDate: startDate,
    EndDate: endDate,
  });
}

export async function fetchProductListPurchase(startDate, endDate) {
  return post('/Orders/GetProductListPurchaseViewWithDateFilter', {
    StartDate: startDate,
    EndDate: endDate,
  });
}

// ── UPS Billing Data (UPS Billing Center "Billing Data" CSV) ─────────────────
// UPS exposes no billing API (confirmed). The only machine path is the
// headerless ~250-column positional CSV. Parsing is done client-side here so
// the raw file (which contains customer PII) never has to be uploaded or
// stored as a file — only the structured rows go to Neon. Field positions were
// derived and numerically validated against a real invoice (net column ties to
// the invoice total to the penny).

// Minimal RFC-4180-ish CSV reader (handles quoted fields + "" escapes + CRLF).
function parseCsvRows(text) {
  const rows = [];
  let row = [], field = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQ = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') inQ = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\r') { /* skip */ }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else field += ch;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

// 0-based column indices in the UPS Billing Data v2.1 layout.
// ⚠️ col 13 is the LEAD SHIPMENT number and col 20 is the PACKAGE TRACKING
// number — NOT the other way round. These were mapped backwards until
// 2026-08-13. On a single-package shipment the two are identical, so the
// error was invisible on ~99% of rows; on a MULTI-PIECE shipment (one
// WorldShip shipment, N boxes, N orders) every package's charges collapsed
// onto the lead tracking number, so one box showed the whole shipment's
// freight while its siblings vanished from the bill entirely. Do not "fix"
// this back. Proven on invoice 000000615R91326 (Bogaert, 3 boxes): after the
// swap two of the three packages reconcile penny-exact against the scale.
// ⚠️ REFERENCES: col 15/16 are the SHIPMENT's two reference fields and col
// 21/22 are the PACKAGE's. On a multi-piece shipment the shipment refs repeat
// the LEAD box's values on every package while the package refs differ per box
// — verified on a real 5-box shipment (invoice ...91216, David Thompson: ship
// ref 936805 on all five, package refs 936805/936802/936803/936158/936804,
// five separate orders). So `pkgRef` is the one to match an order on; shipRef
// is only a fallback. Same trap as the tracking/lead swap above: on a
// single-package shipment all four fields agree, which hides the difference.
// Ref 1 is normally the order number and ref 2 the packer's initials, but
// they're hand-typed and do get transposed.
const UPS_BILL_COLS = {
  version: 0, account: 1, invDate: 4, invNo: 5, currency: 9, invAmt: 10,
  txnDate: 11, shipId: 12, lead: 13, shipRef: 15, shipRef2: 16, tracking: 20,
  pkgRef: 21, pkgRef2: 22, zone: 23,
  entWt: 25, billWt: 26, wtUnit: 27, cat: 43, cls: 44, desc: 45,
  published: 48, incentive: 51, net: 52, dueDate: 62,
  sName: 66, sCompany: 67, sAddr: 68, sCity: 70, sState: 71, sZip: 72,
  rCompany: 74, rName: 75, rAddr1: 76, rAddr2: 77, rCity: 78, rState: 79, rZip: 80,
};

export function parseUpsBillingCsv(text) {
  const raw = parseCsvRows(text)
    .filter(r => r.length > 6 && r.some(c => c && String(c).trim() !== ''));
  if (!raw.length) throw new Error('No data rows found in the file.');
  const g = (r, k) => {
    const v = r[UPS_BILL_COLS[k]];
    return v == null ? '' : String(v).trim();
  };
  const h = raw[0];
  const invoice = {
    invoice_number: g(h, 'invNo'),
    account_number: g(h, 'account'),
    carrier: 'UPS',
    invoice_date: g(h, 'invDate'),
    due_date: g(h, 'dueDate'),
    currency: g(h, 'currency') || 'USD',
    invoice_amount: g(h, 'invAmt'),
    format_version: g(h, 'version'),
  };
  if (!invoice.invoice_number) {
    throw new Error('No invoice number found — make sure this is the UPS "Billing Data" CSV export, not the PDF invoice.');
  }
  // A real UPS Billing Data row starts with a numeric format version like
  // "2.1" — reject anything else so binary/junk can't mis-parse. (Mirror of
  // the guard in netlify/functions/lib/ups-billing-parse.js — change both.)
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(invoice.format_version)) {
    throw new Error('This does not look like a UPS "Billing Data" CSV (unexpected header).');
  }
  const charges = raw.map((r, idx) => ({
    line_no: idx + 1,
    account_number: g(r, 'account'),
    transaction_date: g(r, 'txnDate'),
    tracking_number: g(r, 'tracking'),
    lead_shipment_number: g(r, 'lead'),
    shipment_id: g(r, 'shipId'),
    shipment_ref: g(r, 'shipRef'),
    shipment_ref2: g(r, 'shipRef2'),
    package_ref: g(r, 'pkgRef'),
    package_ref2: g(r, 'pkgRef2'),
    zone: g(r, 'zone'),
    entered_weight: g(r, 'entWt'),
    billed_weight: g(r, 'billWt'),
    weight_unit: g(r, 'wtUnit'),
    charge_category: g(r, 'cat'),
    charge_class_code: g(r, 'cls'),
    charge_description: g(r, 'desc'),
    published_charge: g(r, 'published'),
    incentive_amount: g(r, 'incentive'),
    net_amount: g(r, 'net'),
    sender_name: g(r, 'sName'),
    sender_company: g(r, 'sCompany'),
    sender_city: g(r, 'sCity'),
    sender_state: g(r, 'sState'),
    sender_zip: g(r, 'sZip'),
    receiver_name: g(r, 'rName'),
    receiver_company: g(r, 'rCompany'),
    receiver_address: g(r, 'rAddr1'),
    receiver_address2: g(r, 'rAddr2'),
    receiver_city: g(r, 'rCity'),
    receiver_state: g(r, 'rState'),
    receiver_zip: g(r, 'rZip'),
  }));

  let total = 0;
  const cats = {};
  const pkgs = new Set();
  for (const c of charges) {
    const net = parseFloat(c.net_amount) || 0;
    total += net;
    const k = (c.charge_category || 'OTHER').toUpperCase();
    cats[k] = (cats[k] || 0) + net;
    if (c.tracking_number) pkgs.add(c.tracking_number);
  }
  const round = (n) => Math.round(n * 100) / 100;
  const stats = {
    line_count: charges.length,
    package_count: pkgs.size,
    charge_total: round(total),
    invoice_amount: parseFloat(invoice.invoice_amount) || 0,
    categories: Object.fromEntries(
      Object.entries(cats).map(([k, v]) => [k, round(v)])
    ),
  };
  stats.ties_out = Math.abs(stats.charge_total - stats.invoice_amount) < 0.01;
  return { invoice, charges, stats };
}

export async function importUpsBill(text, fileName, source = 'manual_upload') {
  const { invoice, charges, stats } = parseUpsBillingCsv(text);
  const resp = await neonQuery('ups_bill_import', { invoice, charges, fileName, source });
  return { ...resp, clientStats: stats };
}

export async function fetchUpsBills() {
  return neonQuery('ups_bills_list');
}

// Push the PDF invoice straight to R2 with a presigned PUT (it's a
// hundred-plus pages — far past the ~6MB base64-through-a-function cap) and
// link it to the invoice row. Returns the viewable URL.
export async function uploadUpsBillPdf(invoiceNumber, file) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const presignRes = await fetch(`${base}/r2-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'presign-doc', filename: file.name, prefix: 'ups-bills' }),
  });
  const presign = await presignRes.json();
  if (!presignRes.ok || !presign.uploadUrl) throw new Error(presign.error || 'Could not prepare the PDF upload');

  const put = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presign.contentType },
    body: file,
  });
  if (!put.ok) throw new Error(`PDF upload failed (${put.status})`);

  await neonQuery('ups_bill_attach_pdf', {
    invoice_number: invoiceNumber, pdf_key: presign.key, pdf_filename: file.name,
  });
  return presign.key;
}

// A signed, expiring URL for a stored bill PDF. Not a stable public link —
// these carry customer names and ship-to addresses (see r2-upload).
export async function upsBillPdfUrl(key) {
  if (!key) return null;
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/r2-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'presign-read', key }),
  });
  const data = await res.json();
  if (!res.ok || !data.url) throw new Error(data.error || 'Could not open the PDF');
  return data.url;
}

// ── Vendor approvals (AP) ───────────────────────────────────────────────────
// Scanned invoices ride the same signed-URL path as the UPS bills rather than the
// public r2-image proxy: a crabber invoice is financial paperwork and has no
// business on a stable, guessable URL.
// The paperwork Montel already scanned into the yield sheet, pulled onto the bill
// instead of dragged in again. `list` says what is there, `pull` copies it in.
export async function apYieldDocs(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const res = await fetch(`${base}/ap-yield-docs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not reach the yield sheet (${res.status})`);
  return data;
}

export async function uploadApInvoiceFile(documentId, file, kind = 'invoice', onProgress) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';

  // The hash is what an approval is recorded against, so it has to describe the
  // exact bytes the approver saw. Computed from the file itself, in the browser.
  const buf = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', buf);
  const sha256 = Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0')).join('');

  const presignRes = await fetch(`${base}/r2-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'presign-doc', filename: file.name, prefix: 'ap-invoices' }),
  });
  const presign = await presignRes.json();
  if (!presignRes.ok || !presign.uploadUrl) {
    throw new Error(presign.error || 'Could not prepare the upload');
  }
  // XHR rather than fetch purely for the progress events - a crabber invoice
  // photographed on a phone can be several MB over a plant wifi, and a button that
  // sits there saying nothing reads as broken.
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.uploadUrl, true);
    xhr.setRequestHeader('Content-Type', presign.contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
      ? resolve()
      : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed - check the connection and try again'));
    xhr.send(file);
  });
  if (onProgress) onProgress(100);

  return neonQuery('ap_file_register', {
    documentId, kind, r2Key: presign.key, filename: file.name,
    contentType: presign.contentType, byteSize: file.size, sha256,
  });
}

// Short-lived signed URL for a stored invoice scan.
// The whole bill as one PDF. Derived on demand and keyed by the file set's own
// manifest, so it can never be stale and never becomes a document on the bill.
// Read how many boxes came in off the crabber's invoice, for bills with no trip.
// ── Invoice Approvals ───────────────────────────────────────────────────────
// The context overlay beside an invoice bill (ap-invoice-context.js) and the buyer coach
// (ap-invoice-coach.js). Both need a CPx session and return the server's own error text.
async function postFn(name, params) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const res = await fetch(`${base}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(params || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export function apInvoiceContext(documentId) {
  return postFn('ap-invoice-context', { documentId });
}

// Accounting > Vendors: what each vendor is. list / get / recent read; review / save /
// tell / same write, and each write returns the vendor's fresh card.
export function apVendorProfiles(action, params = {}) {
  return postFn('ap-vendor-profiles', { ...params, action });
}

// Accounting > Cash Outlook. Limited by name on the server (Greg, Matt Sr, Mercedes).
export function cashOutlook(action, params = {}) {
  return postFn('cash-outlook', { ...params, action });
}

// Accounting > Budget. Limited by name on the server (Greg, Mercedes).
export function budgetDoc(action, params = {}) {
  return postFn('budget', { ...params, action });
}

// Accounting > QBD Scans. Limited by name on the server (Greg, Mercedes).
export function qbdScans(action, params = {}) {
  return postFn('qbd-scans', { ...params, action });
}

// Accounting > Authorize.net Deposits. Limited by name on the server (Greg, Mercedes).
export function authnetQbo(action, params = {}) {
  return postFn('authnet-qbo', { ...params, action });
}

// Accounting > Payroll Records. Limited by name on the server (Greg, Mercedes).
export function payrollRecords(action, params = {}) {
  return postFn('payroll', { ...params, action });
}

// Accounting > Payroll Planner. Limited by name on the server (Greg, Mercedes).
export function payrollPlanner(action, params = {}) {
  return postFn('payroll-planner', { ...params, action });
}

// Accounting > Attendance. Page actions are Greg + Mercedes; pip* actions are limited
// to the PIP viewers. Both enforced in netlify/functions/attendance.js.
export function attendance(action, params = {}) {
  return postFn('attendance', { ...params, action });
}

// Product and vendor facts for the buyer coach (shelf life, storage, freight, usage).
export function apBuyFacts(action, params = {}) {
  return postFn('ap-buy-facts', { ...params, action });
}

// Accounting > Buying Coach Settings. Limited by name on the server.
export function apCoachGuidance(action, params = {}) {
  return postFn('ap-coach-guidance', { ...params, action });
}

export function apInvoiceCoach(action, params = {}) {
  return postFn('ap-invoice-coach', { ...params, action });
}

export async function apInvoiceUnits(params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const res = await fetch(`${base}/ap-invoice-units`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(params),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not read the invoices (${res.status})`);
  return data;
}

// Ready to Pay -> QuickBooks: check / read_lines / save_lines / accounts / vendors /
// link_vendor / push / link_existing. See netlify/functions/ap-qbo-push.js.
export async function apQboPush(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const res = await fetch(`${base}/ap-qbo-push`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `QuickBooks request failed (${res.status})`);
  return data;
}

export async function apCombinedPdf(documentId) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const res = await fetch(`${base}/ap-combined-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ documentId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not build the combined copy (${res.status})`);
  // One file needs no merging; the caller opens it the ordinary way.
  if (data.single) return { url: await apFileUrl(data.key), ...data };
  return data;
}

export async function apFileUrl(key) {
  if (!key) return null;
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/r2-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'presign-read', key }),
  });
  const data = await res.json();
  if (!res.ok || !data.url) throw new Error(data.error || 'Could not open that file');
  return data.url;
}

// Recent auto-import (UPS Billing Center email) log for the status line.
export async function fetchUpsBillEmailStatus() {
  const rows = await neonQuery('ups_bill_email_status');
  return Array.isArray(rows) ? rows : [];
}

// Run the email-intake scan now (synchronous POST to the scheduled fn).
// Used by the "Check email now" button so a just-forwarded UPS bill imports
// in seconds instead of waiting for the 3-hourly cron. Returns the run
// summary { checked, imported: [...], skipped, zip, errors }.
export async function triggerUpsBillEmailSync() {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/sync-ups-bill-email-scheduled`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const text = await res.text().catch(() => '');
  let body = null;
  try { body = JSON.parse(text); } catch { /* gateway timeout returns HTML */ }
  if (res.status < 200 || res.status >= 300 || !body) {
    throw new Error(
      res.status === 504 || /timeout/i.test(text)
        ? 'The inbox scan took too long — it will keep running in the background; refresh in a minute.'
        : `Email scan failed (${res.status})`
    );
  }
  return body;
}

// UPS contract pricing config (negotiated terms — stored for analysis).
export async function fetchUpsContractPricing() {
  const rows = await neonQuery('ups_contract_get');
  return Array.isArray(rows) ? rows : [];
}

export async function saveUpsContractPricing(rows) {
  const res = await neonQuery('ups_contract_save', { rows });
  return Array.isArray(res) ? (res[0] || { saved: 0 }) : res;
}

// Realized discount per service from the latest imported UPS bill —
// the actual discount UPS gave, vs the contract's negotiated bands.
export async function fetchUpsBillActualDiscounts() {
  const rows = await neonQuery('ups_bill_actual_discounts');
  return Array.isArray(rows) ? rows : [];
}

export async function fetchUpsBillsSummary() {
  const r = await neonQuery('ups_bills_summary');
  return Array.isArray(r) ? (r[0] || null) : r;
}

// SIM (Special Incentive) credit summary + how many Cost-off rows are
// "explained" (SIM credit and/or $0.01 rounding) and bulk-acceptable.
export async function fetchUpsBillExplainedSummary(invoiceNumber) {
  const r = await neonQuery('ups_bill_explained_summary', { invoice_number: invoiceNumber });
  return Array.isArray(r) ? (r[0] || null) : r;
}

// Bulk-accept the explained Cost-off rows. kind: 'sim' | 'rounding'.
export async function bulkAcceptUpsBillCharges(invoiceNumber, kind, acceptedBy) {
  const r = await neonQuery('ups_bill_bulk_accept', { invoice_number: invoiceNumber, kind, accepted_by: acceptedBy });
  return Array.isArray(r) ? (r[0] || { accepted: 0 }) : r;
}

// ── UPS late-delivery refund/claim tracker (ups-refunds function) ────────────
function upsRefundsBase() {
  return window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
}
async function upsRefundsCall(payload) {
  const res = await fetch(`${upsRefundsBase()}/ups-refunds`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  return res.json();
}
export const startUpsRefundScan = (invoice_number) => upsRefundsCall({ action: 'start-scan', invoice_number });
export const fetchUpsRefundScanStatus = (invoice_number) => upsRefundsCall({ action: 'scan-status', invoice_number });
export const fetchUpsRefunds = (invoice_number) => upsRefundsCall({ action: 'list', invoice_number });
export const setUpsRefundStatus = (invoice_number, tracking_number, status, notes) =>
  upsRefundsCall({ action: 'set-status', invoice_number, tracking_number, status, notes });
// Whole-day late: 'claim' (file a claim) or 'credit' (GSR shipping refund only).
export const setUpsRefundResolution = (invoice_number, tracking_number, mode) =>
  upsRefundsCall({ action: 'set-resolution', invoice_number, tracking_number, mode });
// Match this invoice's GSR credit lines back to refunds/claims we filed earlier.
export const matchUpsRefundCredits = (credits) => upsRefundsCall({ action: 'match-credits', credits });

export async function fetchUpsBillInvoice(invoiceNumber) {
  const r = await neonQuery('ups_bill_get', { invoice_number: invoiceNumber });
  return Array.isArray(r) ? (r[0] || null) : r;
}

// The "extra" charge lines for an invoice — adjustments / corrections /
// zone adjustments / audits (the post-hoc charges not in the scale estimate).
export async function fetchUpsBillExtras(invoiceNumber) {
  return neonQuery('ups_bill_extras', { invoice_number: invoiceNumber });
}

// Account-level charges (no tracking #) + the footing that proves the bill
// reconciles exactly: package_total + account_total = charge_total = invoice.
// Returns a single object (or null) with { invoice_amount, package_total,
// account_total, charge_total, package_count, account_line_count, lines[] }.
export async function fetchUpsBillAccountCharges(invoiceNumber) {
  const r = await neonQuery('ups_bill_account_charges', { invoice_number: invoiceNumber });
  return Array.isArray(r) ? (r[0] || null) : r;
}

// UPS "Summary of Charges" — the four buckets that foot to the invoice total,
// matching the paper bill exactly (WorldShip / Adjustments & Other / Service
// Refund / Service Charges).
export async function fetchUpsBillSummaryOfCharges(invoiceNumber) {
  const r = await neonQuery('ups_bill_summary_of_charges', { invoice_number: invoiceNumber });
  return Array.isArray(r) ? (r[0] || null) : r;
}

export async function fetchUpsBillCharges(invoiceNumber, opts = {}) {
  // opts may include { category, search, tracking, limit, offset }
  return neonQuery('ups_bill_charges', { invoice_number: invoiceNumber, ...opts });
}

// Contract Evaluation — realized levers (per-service transport + fuel + accessorials)
// off one bill, a date range, or all imported bills, for "what-if discount" modeling.
// scope: { invoice_number } | { start, end } | {}  (empty = all bills)
export async function fetchContractEvalData(scope = {}) {
  const r = await neonQuery('ups_contract_eval', scope);
  return Array.isArray(r) ? (r[0] || null) : r;
}

// Reconciliation: one row per tracking number, scale cost vs UPS billed,
// late + surcharge/adjustment flags, status bucket. tolerance in $ (0 = penny-exact).
export async function fetchUpsBillReconciliation(invoiceNumber, opts = {}) {
  return neonQuery('ups_bill_reconcile', {
    invoice_number: invoiceNumber,
    tolerance: opts.tolerance != null ? opts.tolerance : 0,
    status: opts.status || undefined,
    search: opts.search || undefined,
    limit: opts.limit, offset: opts.offset,
  });
}

// Kick off the backfill (Netlify background function — returns 202 fast,
// runs up to 15 min). Progress is polled via fetchUpsBackfillStatus.
export async function startUpsBillBackfill(invoiceNumber) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/ups-bill-backfill-background`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ invoice_number: invoiceNumber }),
  });
  // Background functions return 202 with an empty body; anything 2xx = accepted.
  if (res.status < 200 || res.status >= 300) {
    const t = await res.text().catch(() => '');
    throw new Error(`Backfill could not start (${res.status}) ${t}`.trim());
  }
  return { started: true };
}

export async function fetchUpsBackfillStatus(invoiceNumber) {
  const rows = await neonQuery('ups_backfill_status', { invoice_number: invoiceNumber });
  return Array.isArray(rows) ? (rows[0] || null) : null;
}

// Accept the UPS-billed amount as-is for one or more tracking numbers on a
// bill. Mirrors the legacy admin's auto-reconcile-within-tolerance behavior
// but doesn't touch orders.ship_our_cost — the original scale cost is kept
// for variance trend analysis. items: [{ tracking_number, accepted_amount,
// prior_scale_cost?, variance?, order_id? }].
export async function acceptUpsBillCharges(invoiceNumber, items, opts = {}) {
  return neonQuery('ups_bill_accept', {
    invoice_number: invoiceNumber,
    items,
    accepted_by: opts.accepted_by || null,
    note: opts.note || null,
  });
}

// Undo prior accept(s) for a bill. trackingNumbers = [] / null = whole bill.
export async function unacceptUpsBillCharges(invoiceNumber, trackingNumbers) {
  return neonQuery('ups_bill_unaccept', {
    invoice_number: invoiceNumber,
    tracking_numbers: Array.isArray(trackingNumbers) ? trackingNumbers : [],
  });
}

export async function fetchUpsBillReconSummary(invoiceNumber, tolerance = 0) {
  const rows = await neonQuery('ups_bill_recon_summary', {
    invoice_number: invoiceNumber, tolerance,
  });
  // ROLLUP returns per-status rows + a '_total' row. Reshape to a lookup.
  const out = { byStatus: {}, total: null };
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r.status === '_total') out.total = r;
    else out.byStatus[r.status] = r;
  }
  return out;
}

// Shipping Margin report: UPS billed cost vs what we charged the customer for
// shipping (orders.oshipcost), per tracking #. invoiceNumber null/'' = across
// all imported bills. opts: { status, search, limit, offset }.
export async function fetchUpsShippingMargin(invoiceNumber, opts = {}) {
  return neonQuery('ups_bill_margin', {
    invoice_number: invoiceNumber || null,
    status: opts.status || undefined,
    search: opts.search || undefined,
    limit: opts.limit, offset: opts.offset,
  });
}

export async function fetchUpsShippingMarginSummary(invoiceNumber) {
  const rows = await neonQuery('ups_bill_margin_summary', {
    invoice_number: invoiceNumber || null,
  });
  // ROLLUP returns per-status rows + a '_total' row. Reshape to a lookup.
  const out = { byStatus: {}, total: null };
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (r.status === '_total') out.total = r;
    else out.byStatus[r.status] = r;
  }
  return out;
}

// ── Competitor Price Comparison (Inventory > Price Comparison) ───────────────

export async function fetchPriceComparison(category = 'steamed_blue_crabs') {
  const rows = await neonQuery('comp_price_data', { category });
  return (Array.isArray(rows) && rows[0]) || { category, competitors: [], prices: [], ours: [] };
}

export async function saveCompetitorPrice(row) {
  return neonQuery('comp_price_upsert', row);
}

export async function deleteCompetitorPrice(id) {
  return neonQuery('comp_price_delete', { id });
}

// ─── Daily Profit Per Box (Reports → Daily PPB) ───────────────────────────────
// Matt Sr's daily margin read. Everything is computed server-side in
// lib/ppb-report.js so the page, the email and the CSV can never disagree about
// a number — the page must not re-derive anything it renders.
export async function fetchPpbReport({ date, refresh = false, trendDays = 14 } = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/ppb-report`, {
    method: 'POST', headers,
    body: JSON.stringify({ action: 'preview', date, refresh, trendDays }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `PPB report ${res.status}`);
  return d.report;
}

// ─── PPB Segments (Reports → PPB Segments) ────────────────────────────────────
// Greg's "pull a whole week and segment those into sections" ask (chat 118).
// Same shipping_report_cache source as Daily PPB, rolled across a date range.
export async function fetchPpbSegments({ from, to } = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/ppb-segments`, {
    method: 'POST', headers,
    body: JSON.stringify({ from, to }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `PPB segments ${res.status}`);
  return d.report;
}

// Download the day as CSV. Matt asked to be able to review this outside the
// portal, so the same renderer that builds the email attachment serves it here.
export async function downloadPpbCsv(date) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/ppb-report`, {
    method: 'POST', headers,
    body: JSON.stringify({ action: 'preview', date, refresh: false, format: 'csv' }),
  });
  if (!res.ok) throw new Error(`CSV ${res.status}`);
  const text = await res.text();
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `ppb-${String(date).replace(/\//g, '-')}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Mail the day to a chosen address. Staff-gated server-side: a caller-supplied
// address on this endpoint needs a live session, which is what stops it being a
// way to post a day of customer order values to any inbox on the internet.
export async function emailPpbReport(date, to) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/ppb-report`, {
    method: 'POST', headers,
    body: JSON.stringify({ action: 'send', date, to, refresh: false }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `Send failed (${res.status})`);
  return d;
}

// ── Low-product-score feedback alerts ───────────────────────────────────────
// One call for every action on the Feedback > Low Score Alerts screen. The
// function is staff-gated server-side (each alert names a customer and quotes
// their comment), so the session token is required, not decorative.
export async function feedbackAlert(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/feedback-alert`, {
    method: 'POST', headers,
    body: JSON.stringify({ action, ...params }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `Feedback alerts ${res.status}`);
  return d;
}

// ── Competitor pricing (Procurement > Competitor Pricing) ────────────────
// Matt Sr's hard-crab price benchmark. Staff-gated server-side for `report`,
// `history` and `status` - those put our prices next to our competitors', which
// is our margin position. `run` is deliberately open (the cron reaches it over
// plain HTTP with no session) and throttled instead.
//
// A full five-site scrape goes to the BACKGROUND twin: a synchronous Netlify
// function is capped around 26 seconds by the gateway, and these are other
// people's servers on other people's bad days.
export async function competitorPricing(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const fn = action === 'run' ? 'comp-scrape-background' : 'comp-scrape';
  const res = await fetch(`${base}/${fn}`, {
    method: 'POST', headers,
    body: JSON.stringify({ action, ...params }),
  });
  // A background function answers 202 with no body. There is nothing to read;
  // the caller polls `status` to find out how the run went.
  if (res.status === 202) return { queued: true };
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `Competitor pricing ${res.status}`);
  return d;
}

// ── Feedback by product ─────────────────────────────────────────────────────
// Staff-gated server-side: every row names a customer, quotes their comment and
// links their order.
export async function productFeedbackApi(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/product-feedback`, {
    method: 'POST', headers,
    body: JSON.stringify({ action, ...params }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `Product feedback ${res.status}`);
  return d;
}

// ── UPS Delay Tracker (Communications > Delay Tracker) ──────────────────────
// Rachael's replacement for the hand-kept Delays.xlsx. Staff-gated server-side:
// every row names a customer, their order and where their package went wrong, so
// there is no anonymous read here and the session token is required, not
// decorative.
export async function delayTracker(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/delay-tracker`, {
    method: 'POST', headers,
    body: JSON.stringify({ action, ...params }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `Delay tracker ${res.status}`);
  return d;
}

// ── Crab & Cruise event copy (Marketing > C&C Event Copy) ───────────────
// Generates the Facebook event title + description from Event Management, the
// record of truth. Staff-gated server-side: it returns ticket prices and the
// inventory left on each tier.
export async function cncEventCopy(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/cnc-event-copy`, {
    method: 'POST', headers,
    body: JSON.stringify({ action, ...params }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `C&C event copy ${res.status}`);
  return d;
}

// Sidebar favorites (star a menu entry, float it to the top). Per person, server-side so
// the stars follow them between desktop and phone. See netlify/functions/nav-favorites.js.
export async function navFavorites(action = 'list', params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/nav-favorites`, { method: 'POST', headers, body: JSON.stringify({ action, ...params }) });
  if (!res.ok) throw new Error(`nav-favorites ${res.status}`);
  return res.json();
}

// Customer Analysis report: one customer's whole history (netlify/functions/
// customer-analysis.js). q = email, customer number or order number.
export async function customerAnalysis(q) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/customer-analysis`, { method: 'POST', headers, body: JSON.stringify({ q }) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.error) throw new Error(d.error || `Customer analysis ${res.status}`);
  return d;
}

// The dashboard Feedback tile (Greg, Oct 20, 2026): the same figures the live admin's
// Feedback card shows, from the same function it calls. Counts are public; anything with
// names (photos, review-site customers, low-score reviews) needs this CPx session.
//   counts:   feedbackExtras(ymd)                          -> { photos, review_sites }
//   table:    feedbackExtras(ymd, { categories: 1, full: 1 }) -> { rows, low, crab_legs }
//   detail:   feedbackExtras(ymd, { detail: 'photos' | 'reviews' | 'low', category })
// Our public rating on Google, Facebook and Trustpilot, with the change over about 30 days
// (Greg, Oct 21, 2026). Public numbers; no session needed.
export async function reviewSiteScores() {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const res = await fetch(`${base}/review-site-scores`);
  const body = await res.json().catch(() => null);
  if (!res.ok || !body) throw new Error((body && body.error) || `Could not load review site scores (${res.status}).`);
  return body.sites || [];
}

export async function feedbackExtras(ymd, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';
  const qs = new URLSearchParams({ since: ymd });
  Object.entries(params).forEach(([k, v]) => { if (v != null) qs.set(k, String(v)); });
  const token = getAccessToken();
  const headers = {};
  if (params.detail && token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${base}/feedback-extras?${qs}`, { headers });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error((body && body.error) || `Could not load this (${res.status}).`);
  return body;
}
